// Chemin: C:\gestion-copro\src\pages\admin-saas\CrmPipelinePage.tsx
// Session 192 : Refonte CRM — conteneur à 4 onglets (Pipeline, Messagerie, Prospects, Tableau de bord)
// Session 193 : Refonte classification prospects (Tier → Segment), fix bug brouillon panel slide-in
// Session 194 : ModalDetailMail dans OngletMessagerie — brouillon ouvrable, envoi Gmail, marquer envoyé
// Session 207 : Ajout onglet "Actions du jour" — 4 sections filtrées par responsable
// Session 220 : Prompt SENTINELLE Prospection V3 intégré — Guide réécrit (3 phases, 2 livrables HTML)
// Session 220 (suite) : Bug fix handleOuvrirGmail (window.open → sendEmailCrm) + pièce jointe PDF présentation

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  User,
  Phone,
  Mail,
  Globe,
  MapPin,
  ChevronDown,
  ChevronUp,
  GripVertical,
  MessageSquarePlus,
  Trash2,
  Edit3,
  X,
  Check,
  Users,
  Building2,
  Megaphone,
  ExternalLink,
  StickyNote,
  RefreshCw,
  ArrowRightLeft,
  Target,
  Handshake,
  AlertTriangle,
  CalendarClock,
  Crosshair,
  Ruler,
  Sparkles,
  Send,
  Copy,
  RotateCcw,
  LayoutDashboard,
  Inbox,
  List,
  Kanban,
  TrendingUp,
  Clock,
  CheckCircle2,
  Filter,
  Eye,
  ChevronRight,
  BookOpen,
  AlertCircle,
  Zap,
  FileText,
  Loader2,
} from 'lucide-react';
import {
  Prospect,
  StatutProspect,
  TypeProspect,
  TypeCibleProspect,
  ResponsableProspect,
  SegmentProspect,
  NoteSuivi,
  HistoriqueMailProspect,
  InfoResponsable,
  LABELS_STATUT_PROSPECT,
  COULEURS_STATUT_PROSPECT,
  COLONNES_KANBAN,
  LABELS_TYPE_CIBLE,
  LABELS_TYPE_PROSPECT,
  LABELS_TYPE_TEMPLATE,
  LABELS_SEGMENT_PROSPECT,
  COULEURS_SEGMENT_PROSPECT,
  DESCRIPTIONS_SEGMENT_PROSPECT,
  TypeTemplateMail,
} from '../../types/crm.types';
import {
  chargerProspects,
  chargerResponsables,
  getLabelResponsable,
  getCouleurResponsable,
  creerProspect,
  changerStatutProspect,
  changerResponsableProspect,
  mettreAJourProspect,
  supprimerProspect,
  ajouterNote,
  rapprocherProspectUser,
  purgerTousLesProspects,
  ajouterHistoriqueMail,
  marquerMailEnvoye,
  supprimerMailHistorique,
} from '../../services/crm.service';
import { Timestamp, doc, getDoc } from 'firebase/firestore';
import CreerApporteurModal from '../../components/admin/CreerApporteurModal';
import { db } from '../../services/firebase';
import {
  genererMailProspection,
  ResultatMailProspection,

  getGmailAuthUrlCrm,
} from '../../services/prospection.service';
import { seedProspectsInitiaux } from '../../services/crm.seed';

// ============================================
// TYPES ONGLETS
// ============================================

type OngletCRM = 'pipeline' | 'actions-du-jour' | 'messagerie' | 'prospects' | 'tableau-bord' | 'guide';

// ============================================
// ICÔNES PAR TYPE DE PROSPECT
// ============================================

const ICONES_TYPE_PROSPECT: Record<TypeProspect, React.ReactNode> = {
  association: <Users size={14} className="text-purple-500" />,
  copropriete: <Building2 size={14} className="text-blue-500" />,
  individu: <User size={14} className="text-green-500" />,
  prescripteur: <Megaphone size={14} className="text-amber-500" />,
};

// ============================================
// SUGGESTIONS DE PROBLÉMATIQUES
// ============================================

const SUGGESTIONS_PROBLEMATIQUES = [
  'Syndic opaque / manque de transparence',
  'Charges excessives ou injustifiées',
  'Travaux mal gérés ou non votés',
  'Absence de communication du syndic',
  'AG mal organisées ou non conformes',
  'Impayés importants dans la copropriété',
  'Dégradation des parties communes',
  'Conflit avec le promoteur / constructeur',
  'Problèmes de garantie décennale / GPA',
  'Syndic bénévole débordé',
];

// ============================================
// COMPOSANT CARTE PROSPECT (Kanban)
// Session 206 : badges alerte (inactivité, réponse en attente, action due)
// ============================================

// Calcul dernière activité (dernier mail ou date création)
const getDerniereActivite = (prospect: Prospect): Date => {
  const mails = prospect.historiqueMails || [];
  if (mails.length > 0) {
    const dates = mails
      .map((m) => m.date?.toDate?.() || new Date(m.date as unknown as string))
      .filter((d) => !isNaN(d.getTime()));
    if (dates.length > 0) return new Date(Math.max(...dates.map((d) => d.getTime())));
  }
  return prospect.createdAt?.toDate?.() || new Date(prospect.createdAt as unknown as string) || new Date();
};

// Vérifie si une réponse importée n'a pas encore de relance générée
const hasReponseEnAttente = (prospect: Prospect): boolean => {
  const mails = prospect.historiqueMails || [];
  const reponses = mails.filter((m) => (m as any).type === 'recu');
  if (reponses.length === 0) return false;
  const derniereReponse = reponses[reponses.length - 1];
  // Pas de relance = aucun mail envoyé après la dernière réponse
  const dateReponse = derniereReponse.date?.toDate?.() || new Date(derniereReponse.date as unknown as string);
  const relanceApres = mails.some((m) => {
    if ((m as any).type === 'recu') return false;
    const d = m.date?.toDate?.() || new Date(m.date as unknown as string);
    return d > dateReponse;
  });
  return !relanceApres;
};

// Vérifie si une action planifiée est due (aujourd'hui ou passée)
const hasActionDue = (prospect: Prospect): boolean => {
  if (!prospect.prochaineAction) return false;
  const texte = prospect.prochaineAction.toLowerCase();
  // Détecter une date dans le texte (format jj/mm/aaaa ou jj-mm-aaaa)
  const match = texte.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
  if (!match) return false;
  const dateAction = new Date(parseInt(match[3]), parseInt(match[2]) - 1, parseInt(match[1]));
  return dateAction <= new Date();
};

interface CarteProspectProps {
  prospect: Prospect;
  responsables: InfoResponsable[];
  onDragStart: (e: React.DragEvent, prospect: Prospect) => void;
  onOuvrirDetail: (prospect: Prospect) => void;
  onChangerResponsable: (prospectId: string, responsable: ResponsableProspect) => void;
}

const CarteProspect: React.FC<CarteProspectProps> = ({
  prospect,
  responsables,
  onDragStart,
  onOuvrirDetail,
  onChangerResponsable,
}) => {
  const nbNotes = prospect.notes?.length || 0;
  const nbProblematiques = prospect.problematiques?.length || 0;

  // Calcul alertes
  const derniereActivite = getDerniereActivite(prospect);
  const joursInactivite = Math.floor((Date.now() - derniereActivite.getTime()) / (1000 * 60 * 60 * 24));
  const alerteInactivite = joursInactivite >= 14;
  const alerteReponse = hasReponseEnAttente(prospect);
  const alerteAction = hasActionDue(prospect);

  const handleCycleResponsable = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (responsables.length <= 1) return;
    const currentIndex = responsables.findIndex((r) => r.identifiant === prospect.responsable);
    const nextIndex = (currentIndex + 1) % responsables.length;
    onChangerResponsable(prospect.id, responsables[nextIndex].identifiant);
  };

  const tierBordure: Record<string, string> = {
    ambassadeur:   'border-l-4 border-l-emerald-500',
    client_direct: 'border-l-4 border-l-purple-400',
    recommandeur:  'border-l-4 border-l-blue-400',
  };
  const tierBadge: Record<string, string> = {
    ambassadeur:   'bg-emerald-100 text-emerald-700 border-emerald-300',
    client_direct: 'bg-purple-100 text-purple-700 border-purple-300',
    recommandeur:  'bg-blue-100 text-blue-700 border-blue-300',
  };
  const bordureClasse = prospect.segment ? tierBordure[prospect.segment] : 'border-l-4 border-l-gray-200';

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, prospect)}
      onClick={() => onOuvrirDetail(prospect)}
      className={`bg-white rounded-lg shadow-sm border border-gray-200 p-3 cursor-pointer hover:shadow-md hover:border-emerald-300 transition-all group ${bordureClasse}`}
    >
      <div className="flex items-start gap-2">
        <GripVertical
          size={16}
          className="text-gray-300 mt-0.5 flex-shrink-0 cursor-grab opacity-0 group-hover:opacity-100 transition-opacity"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-1.5">
              {ICONES_TYPE_PROSPECT[prospect.typeProspect]}
              <span className="text-xs text-gray-400 font-medium">
                {LABELS_TYPE_PROSPECT[prospect.typeProspect]}
              </span>
            </div>
            {prospect.segment ? (
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold border ${tierBadge[prospect.segment]}`}>
                {LABELS_SEGMENT_PROSPECT[prospect.segment as SegmentProspect]}
              </span>
            ) : (
              <span className="text-[10px] px-1.5 py-0.5 rounded font-bold border border-gray-200 bg-gray-50 text-gray-400">
                —
              </span>
            )}
          </div>
          <h4 className="font-semibold text-sm text-gray-800 truncate">{prospect.nom}</h4>
        </div>
      </div>

      {prospect.contactNom && (
        <p className="text-xs text-gray-500 mt-1.5 truncate flex items-center gap-1">
          <User size={11} />
          {prospect.contactNom}
        </p>
      )}

      {prospect.prochaineAction && (
        <p className="text-xs text-amber-600 mt-1 truncate flex items-center gap-1">
          <CalendarClock size={11} />
          {prospect.prochaineAction}
        </p>
      )}

      <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
        <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded font-medium">
          {LABELS_TYPE_CIBLE[prospect.typeCible]?.split('(')[0]?.trim() || prospect.typeCible}
        </span>
        <button
          onClick={handleCycleResponsable}
          className={`text-[10px] px-1.5 py-0.5 rounded font-semibold transition-colors ${getCouleurResponsable(prospect.responsable, responsables)} hover:opacity-80`}
          title="Cliquer pour changer de responsable"
        >
          {getLabelResponsable(prospect.responsable, responsables).split(' ')[0]}
        </button>
      </div>

      <div className="flex items-center gap-3 mt-1.5">
        {nbNotes > 0 && (
          <span title={`${nbNotes} note${nbNotes > 1 ? 's' : ''} de suivi`} className="flex items-center gap-1 text-[10px] text-gray-400 cursor-help">
            <StickyNote size={10} />
            {nbNotes}
          </span>
        )}
        {nbProblematiques > 0 && (
          <span title={`${nbProblematiques} problématique${nbProblematiques > 1 ? 's' : ''} identifiée${nbProblematiques > 1 ? 's' : ''}`} className="flex items-center gap-1 text-[10px] text-red-400 cursor-help">
            <AlertTriangle size={10} />
            {nbProblematiques}
          </span>
        )}
        {(prospect.historiqueMails?.length || 0) > 0 && (
          <span title={`${prospect.historiqueMails!.length} mail${prospect.historiqueMails!.length > 1 ? 's' : ''} généré${prospect.historiqueMails!.length > 1 ? 's' : ''}`} className="flex items-center gap-1 text-[10px] text-blue-400 cursor-help">
            <Mail size={10} />
            {prospect.historiqueMails!.length}
          </span>
        )}
      </div>

      {/* Badges alerte */}
      {(alerteInactivite || alerteReponse || alerteAction) && (
        <div className="flex flex-wrap gap-1 mt-2 pt-1.5 border-t border-gray-100">
          {alerteAction && (
            <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold" title="Action planifiée due ou en retard">
              <AlertTriangle size={9} /> Action due
            </span>
          )}
          {alerteReponse && (
            <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold" title="Réponse reçue — relance non encore générée">
              <Mail size={9} /> Relance à faire
            </span>
          )}
          {alerteInactivite && !alerteReponse && (
            <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold" title={`Aucune activité depuis ${joursInactivite} jours`}>
              <CalendarClock size={9} /> {joursInactivite}j sans contact
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================
// COMPOSANT COLONNE KANBAN
// ============================================

interface ColonneKanbanProps {
  statut: StatutProspect;
  prospects: Prospect[];
  responsables: InfoResponsable[];
  onDragStart: (e: React.DragEvent, prospect: Prospect) => void;
  onDrop: (statut: StatutProspect) => void;
  onOuvrirDetail: (prospect: Prospect) => void;
  onChangerResponsable: (prospectId: string, responsable: ResponsableProspect) => void;
}

const ColonneKanban: React.FC<ColonneKanbanProps> = ({
  statut,
  prospects,
  responsables,
  onDragStart,
  onDrop,
  onOuvrirDetail,
  onChangerResponsable,
}) => {
  const [dragOver, setDragOver] = useState(false);

  return (
    <div
      className={`flex flex-col min-w-[260px] max-w-[300px] rounded-xl border-2 transition-colors ${
        dragOver ? 'border-emerald-400 bg-emerald-50/50' : COULEURS_STATUT_PROSPECT[statut]
      }`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); onDrop(statut); }}
    >
      <div className="px-3 py-2.5 border-b border-gray-200/50">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-sm text-gray-700">{LABELS_STATUT_PROSPECT[statut]}</h3>
          <span className="text-xs bg-white/80 text-gray-500 px-2 py-0.5 rounded-full font-semibold shadow-sm">
            {prospects.length}
          </span>
        </div>
      </div>

      <div className="p-2 space-y-2 flex-1 overflow-y-auto max-h-[calc(100vh-320px)]">
        {prospects.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <p className="text-xs">Aucun prospect</p>
          </div>
        ) : (() => {
          // Regrouper par segment pour afficher des séparateurs
          const groupes: { segment: string; label: string; couleur: string; items: Prospect[] }[] = [
            { segment: 'ambassadeur', label: 'Ambassadeurs', couleur: 'text-emerald-600 border-emerald-200', items: prospects.filter(p => p.segment === 'ambassadeur') },
            { segment: 'client_direct', label: 'Clients directs', couleur: 'text-purple-600 border-purple-200', items: prospects.filter(p => p.segment === 'client_direct') },
            { segment: 'recommandeur', label: 'Recommandeurs', couleur: 'text-blue-600 border-blue-200', items: prospects.filter(p => p.segment === 'recommandeur') },
            { segment: '', label: 'Non classés', couleur: 'text-gray-400 border-gray-200', items: prospects.filter(p => !p.segment) },
          ].filter(g => g.items.length > 0);

          return groupes.map((groupe, idx) => (
            <div key={groupe.segment || 'none'}>
              {/* Séparateur de groupe — sauf si un seul groupe */}
              {groupes.length > 1 && (
                <div className={`flex items-center gap-1.5 px-1 mb-1.5 ${idx > 0 ? 'mt-3' : ''}`}>
                  <span className={`text-[10px] font-bold uppercase tracking-wider ${groupe.couleur.split(' ')[0]}`}>
                    {groupe.label}
                  </span>
                  <div className={`flex-1 border-t ${groupe.couleur.split(' ')[1]}`} />
                  <span className={`text-[10px] font-semibold ${groupe.couleur.split(' ')[0]}`}>{groupe.items.length}</span>
                </div>
              )}
              {groupe.items.map((prospect) => (
                <div key={prospect.id} className="mb-2">
                  <CarteProspect
                    prospect={prospect}
                    responsables={responsables}
                    onDragStart={onDragStart}
                    onOuvrirDetail={onOuvrirDetail}
                    onChangerResponsable={onChangerResponsable}
                  />
                </div>
              ))}
            </div>
          ));
        })()}
      </div>
    </div>
  );
};

// ============================================
// MODAL CRÉATION / ÉDITION PROSPECT
// ============================================

interface ModalProspectProps {
  prospect?: Prospect | null;
  responsables: InfoResponsable[];
  onFermer: () => void;
  onSauvegarder: (data: {
    nom: string;
    typeProspect: TypeProspect;
    typeCible: TypeCibleProspect;
    segment: SegmentProspect;
    responsable: ResponsableProspect;
    contactNom?: string;
    contactEmail?: string;
    contactTelephone?: string;
    adresse?: string;
    siteWeb?: string;
    source?: string;
    noteInitiale?: string;
    problematiques?: string[];
    objectifContact?: string;
    tailleStructure?: string;
    prochaineAction?: string;
    dateProchaineAction?: Timestamp;
  }) => Promise<void>;
  onSupprimer?: (prospectId: string) => Promise<void>;
}

const ModalProspect: React.FC<ModalProspectProps> = ({
  prospect,
  responsables,
  onFermer,
  onSauvegarder,
  onSupprimer,
}) => {
  const [nom, setNom] = useState(prospect?.nom || '');
  const [typeProspect, setTypeProspect] = useState<TypeProspect>(prospect?.typeProspect || 'association');
  const [typeCible, setTypeCible] = useState<TypeCibleProspect>(prospect?.typeCible || 'vigie');
  const [segment, setSegment] = useState<SegmentProspect>(prospect?.segment || 'ambassadeur');
  const [responsable, setResponsable] = useState<ResponsableProspect>(
    prospect?.responsable || (responsables.length > 0 ? responsables[0].identifiant : '')
  );
  const [contactNom, setContactNom] = useState(prospect?.contactNom || '');
  const [contactEmail, setContactEmail] = useState(prospect?.contactEmail || '');
  const [contactTelephone, setContactTelephone] = useState(prospect?.contactTelephone || '');
  const [adresse, setAdresse] = useState(prospect?.adresse || '');
  const [siteWeb, setSiteWeb] = useState(prospect?.siteWeb || '');
  const [source, setSource] = useState(prospect?.source || '');
  const [noteInitiale, setNoteInitiale] = useState('');
  const [problematiques, setProblematiques] = useState<string[]>(prospect?.problematiques || []);
  const [nouvelleProblematique, setNouvelleProblematique] = useState('');
  const [objectifContact, setObjectifContact] = useState(prospect?.objectifContact || '');
  const [tailleStructure, setTailleStructure] = useState(prospect?.tailleStructure || '');
  const [prochaineAction, setProchaineAction] = useState(prospect?.prochaineAction || '');
  const [dateProchaineAction, setDateProchaineAction] = useState(
    prospect?.dateProchaineAction
      ? new Date(prospect.dateProchaineAction.toDate()).toISOString().split('T')[0]
      : ''
  );
  const [saving, setSaving] = useState(false);
  const [confirmSupp, setConfirmSupp] = useState(false);

  const ajouterProblematique = (texte: string) => {
    const trimmed = texte.trim();
    if (trimmed && !problematiques.includes(trimmed)) {
      setProblematiques([...problematiques, trimmed]);
    }
    setNouvelleProblematique('');
  };

  const retirerProblematique = (index: number) => {
    setProblematiques(problematiques.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!nom.trim()) return;
    setSaving(true);
    try {
      await onSauvegarder({
        nom, typeProspect, typeCible, segment, responsable,
        contactNom: contactNom || undefined,
        contactEmail: contactEmail || undefined,
        contactTelephone: contactTelephone || undefined,
        adresse: adresse || undefined,
        siteWeb: siteWeb || undefined,
        source: source || undefined,
        noteInitiale: noteInitiale || undefined,
        problematiques,
        objectifContact: objectifContact || undefined,
        tailleStructure: tailleStructure || undefined,
        prochaineAction: prochaineAction || undefined,
        dateProchaineAction: dateProchaineAction ? Timestamp.fromDate(new Date(dateProchaineAction)) : undefined,
      });
      onFermer();
    } catch (err) {
      console.error('[CRM] Erreur sauvegarde:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSupprimer = async () => {
    if (!prospect || !onSupprimer) return;
    await onSupprimer(prospect.id);
    onFermer();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-bold text-gray-800">
            {prospect ? 'Modifier le prospect' : 'Nouveau prospect'}
          </h2>
          <button onClick={onFermer} className="p-1 hover:bg-gray-100 rounded"><X size={20} /></button>
        </div>

        <div className="p-4 space-y-4">
          {/* Nom */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Nom / Raison sociale *</label>
            <input
              type="text" value={nom} onChange={(e) => setNom(e.target.value)} autoFocus
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Ex: ARC Hauts-de-France"
            />
          </div>

          {/* Type + Cible */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Type</label>
              <select value={typeProspect} onChange={(e) => setTypeProspect(e.target.value as TypeProspect)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500">
                {Object.entries(LABELS_TYPE_PROSPECT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Produit visé</label>
              <select value={typeCible} onChange={(e) => setTypeCible(e.target.value as TypeCibleProspect)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500">
                {Object.entries(LABELS_TYPE_CIBLE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>

          {/* Segment stratégique */}
          <div>
            <label className="block text-xs text-gray-500 mb-2">Segment stratégique</label>
            <div className="space-y-2">
              {(['ambassadeur', 'client_direct', 'recommandeur'] as SegmentProspect[]).map((t) => (
                <button key={t} type="button" onClick={() => setSegment(t)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg border-2 transition-colors ${
                    segment === t
                      ? COULEURS_SEGMENT_PROSPECT[t] + ' border-current'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300 bg-gray-50'
                  }`}>
                  <p className="font-bold text-xs">{LABELS_SEGMENT_PROSPECT[t]}</p>
                  <p className="text-[10px] opacity-70 mt-0.5 leading-tight">{DESCRIPTIONS_SEGMENT_PROSPECT[t]}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Responsable */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Responsable</label>
            <select value={responsable} onChange={(e) => setResponsable(e.target.value as ResponsableProspect)}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500">
              {responsables.map((r) => <option key={r.identifiant} value={r.identifiant}>{r.nomComplet}</option>)}
            </select>
          </div>

          {/* Contact */}
          <div className="border-t pt-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Contact</p>
            <div className="space-y-2">
              <input type="text" value={contactNom} onChange={(e) => setContactNom(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                placeholder="Nom du contact (Président, Directeur...)" />
              <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                placeholder="Email" />
              <input type="tel" value={contactTelephone} onChange={(e) => setContactTelephone(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                placeholder="Téléphone" />
              <input type="text" value={adresse} onChange={(e) => setAdresse(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                placeholder="Adresse / Ville" />
              <input type="url" value={siteWeb} onChange={(e) => setSiteWeb(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                placeholder="Site web (https://...)" />
            </div>
          </div>

          {/* Prospection SENTINELLE */}
          <div className="border-t pt-4 bg-amber-50/30 rounded-lg p-3">
            <p className="text-xs font-semibold text-amber-600 uppercase tracking-wider mb-3">Données SENTINELLE</p>

            <div className="mb-3">
              <label className="block text-xs text-gray-500 mb-1">Problématiques identifiées</label>
              <div className="flex flex-wrap gap-1 mb-2">
                {problematiques.map((p, i) => (
                  <span key={i} className="flex items-center gap-1 text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded border border-red-200">
                    {p}
                    <button onClick={() => retirerProblematique(i)} className="hover:text-red-900"><X size={10} /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input type="text" value={nouvelleProblematique}
                  onChange={(e) => setNouvelleProblematique(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && ajouterProblematique(nouvelleProblematique)}
                  className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                  placeholder="Saisir ou choisir ci-dessous..." />
                <button onClick={() => ajouterProblematique(nouvelleProblematique)}
                  disabled={!nouvelleProblematique.trim()}
                  className="px-3 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 disabled:opacity-30 transition-colors">
                  <Plus size={16} />
                </button>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {SUGGESTIONS_PROBLEMATIQUES.filter((s) => !problematiques.includes(s)).slice(0, 5).map((suggestion) => (
                  <button key={suggestion} onClick={() => ajouterProblematique(suggestion)}
                    className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-500 rounded hover:bg-amber-100 hover:text-amber-700 transition-colors">
                    + {suggestion.length > 35 ? suggestion.substring(0, 35) + '...' : suggestion}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-3">
              <label className="block text-xs text-gray-500 mb-1">Objectif de la prise de contact</label>
              <input type="text" value={objectifContact} onChange={(e) => setObjectifContact(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                placeholder="Ex: Partenariat de recommandation, abonnement CS, essai VIGIE..." />
            </div>

            <div className="mb-3">
              <label className="block text-xs text-gray-500 mb-1">Taille / Nombre d'adhérents ou de lots</label>
              <input type="text" value={tailleStructure} onChange={(e) => setTailleStructure(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                placeholder="Ex: 3 500 adhérents, 45 lots, 12 copropriétés..." />
            </div>

            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Prochaine action</label>
                <input type="text" value={prochaineAction} onChange={(e) => setProchaineAction(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                  placeholder="Ex: Appeler, envoyer démo, relancer..." />
              </div>
              <div className="w-40">
                <label className="block text-xs text-gray-500 mb-1">Date prévue</label>
                <input type="date" value={dateProchaineAction} onChange={(e) => setDateProchaineAction(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-amber-500" />
              </div>
            </div>
          </div>

          {/* Source + Note */}
          <div className="border-t pt-4">
            <div className="mb-3">
              <label className="block text-xs text-gray-500 mb-1">Source (comment identifié ?)</label>
              <input type="text" value={source} onChange={(e) => setSource(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                placeholder="Ex: Recherche web, salon, réseau..." />
            </div>
            {!prospect && (
              <div>
                <label className="block text-xs text-gray-500 mb-1">Note initiale (optionnel)</label>
                <textarea value={noteInitiale} onChange={(e) => setNoteInitiale(e.target.value)} rows={2}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 resize-none"
                  placeholder="Contexte, infos utiles..." />
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between p-4 border-t bg-gray-50 rounded-b-xl">
          <div>
            {prospect && onSupprimer && (
              confirmSupp ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-red-600 font-semibold">Supprimer ?</span>
                  <button onClick={handleSupprimer} className="px-3 py-1.5 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700">Confirmer</button>
                  <button onClick={() => setConfirmSupp(false)} className="px-3 py-1.5 bg-gray-200 text-gray-600 text-xs rounded-lg hover:bg-gray-300">Non</button>
                </div>
              ) : (
                <button onClick={() => setConfirmSupp(true)} className="flex items-center gap-1 text-xs text-red-400 hover:text-red-600 transition-colors">
                  <Trash2 size={14} /> Supprimer
                </button>
              )
            )}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onFermer} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-200 rounded-lg transition-colors">Annuler</button>
            <button onClick={handleSubmit} disabled={!nom.trim() || saving}
              className="px-6 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors">
              {saving ? 'Enregistrement...' : prospect ? 'Modifier' : 'Créer le prospect'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


// ============================================
// MODAL MAIL SENTINELLE
// ============================================

interface ModalMailSentinelleProps {
  prospect: Prospect;
  onFermer: () => void;
  onMailGenere: (prospectId: string) => void;
  onGmailOuvert?: (prospect: Prospect) => void;
}

const ModalMailSentinelle: React.FC<ModalMailSentinelleProps> = ({ prospect, onFermer, onMailGenere, onGmailOuvert }) => {
  const [template, setTemplate] = useState<TypeTemplateMail>('premier_contact');
  const [instructionsLibres, setInstructionsLibres] = useState('');
  const [generation, setGeneration] = useState(false);
  const [resultat, setResultat] = useState<ResultatMailProspection | null>(null);
  const [erreur, setErreur] = useState('');
  const [copie, setCopie] = useState(false);
  const [envoiOk, setEnvoiOk] = useState(false);
  const [contenuEdite, setContenuEdite] = useState<string>('');

  useEffect(() => {
    if (prospect.historiqueMails?.length > 0) {
      setTemplate('relance_demo');
    } else if (prospect.segment === 'ambassadeur') {
      setTemplate('proposition_partenariat');
    } else {
      setTemplate('premier_contact');
    }
  }, [prospect]);

  const handleGenerer = async () => {
    setGeneration(true);
    setErreur('');
    setResultat(null);
    setEnvoiOk(false);
    try {
      const mail = await genererMailProspection(prospect.id, template, instructionsLibres || undefined);
      setResultat(mail);
      setContenuEdite(mail.contenuTexte || '');
    } catch (err: unknown) {
      setErreur(err instanceof Error ? err.message : 'Erreur lors de la génération.');
    } finally {
      setGeneration(false);
    }
  };

  const handleCopierHtml = () => {
    if (!resultat) return;
    navigator.clipboard.writeText(resultat.contenuHtml);
    setCopie(true);
    setTimeout(() => setCopie(false), 2000);
  };

  const handleEnregistrer = async () => {
    if (!resultat) return;
    try {
      await ajouterHistoriqueMail(prospect.id, {
        objet: resultat.objet,
        template,
        destinataire: prospect.contactEmail || '',
        auteur: prospect.responsable,
        envoye: false,
        apercu: resultat.contenuTexte,
        contenuHtml: resultat.contenuHtml,
      });
      onMailGenere(prospect.id);
      onFermer();
    } catch (err) {
      console.error('[CRM] Erreur enregistrement mail:', err);
    }
  };

  const handleOuvrirGmail = async () => {
    if (!resultat || !prospect.contactEmail) return;
    const to = encodeURIComponent(prospect.contactEmail);
    const subject = encodeURIComponent(resultat.objet);
    const body = encodeURIComponent(contenuEdite || resultat.contenuTexte || '');
    window.open(`https://mail.google.com/mail/?view=cm&to=${to}&su=${subject}&body=${body}`, '_blank');
    // Enregistrer directement comme envoyé
    try {
      await ajouterHistoriqueMail(prospect.id, {
        objet: resultat.objet,
        template,
        destinataire: prospect.contactEmail || '',
        auteur: prospect.responsable,
        envoye: true,
        apercu: contenuEdite || resultat.contenuTexte,
        contenuHtml: resultat.contenuHtml,
      });
      onMailGenere(prospect.id);
    } catch (err) {
      console.error('[CRM] Erreur enregistrement mail envoyé:', err);
    }
    setEnvoiOk(true);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-xl">
          <div className="flex items-center gap-2 text-white">
            <Sparkles size={20} />
            <h2 className="font-bold">SENTINELLE — Mail pour {prospect.nom}</h2>
          </div>
          <button onClick={onFermer} className="p-1 text-white/80 hover:text-white rounded"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {!resultat && (
            <>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Type de mail</label>
                <div className="grid grid-cols-2 gap-2">
                  {([
                    { value: 'premier_contact' as TypeTemplateMail, label: 'Premier contact', desc: 'Première prise de contact' },
                    { value: 'relance_demo' as TypeTemplateMail, label: 'Relance / Démo', desc: 'Après un échange' },
                    { value: 'proposition_partenariat' as TypeTemplateMail, label: 'Partenariat', desc: 'Pour associations' },
                    { value: 'libre' as TypeTemplateMail, label: 'Rédaction libre', desc: 'Consignes personnalisées' },
                  ]).map((t) => (
                    <button key={t.value} onClick={() => setTemplate(t.value)}
                      className={`text-left p-3 rounded-lg border-2 transition-colors ${template === t.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      <p className="text-sm font-semibold text-gray-800">{t.label}</p>
                      <p className="text-xs text-gray-500">{t.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {template === 'libre' && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Consignes pour SENTINELLE</label>
                  <textarea value={instructionsLibres} onChange={(e) => setInstructionsLibres(e.target.value)} rows={3}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 resize-none"
                    placeholder="Ex: Insister sur le fait qu'on peut se rencontrer au salon X la semaine prochaine..." />
                </div>
              )}

              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Contexte envoyé à SENTINELLE</p>
                <div className="text-xs text-gray-600 space-y-0.5">
                  <p><span className="font-semibold">Destinataire :</span> {prospect.contactNom || prospect.nom} ({prospect.contactEmail})</p>
                  {prospect.problematiques?.length > 0 && <p><span className="font-semibold">Problématiques :</span> {prospect.problematiques.join(', ')}</p>}
                  {prospect.objectifContact && <p><span className="font-semibold">Objectif :</span> {prospect.objectifContact}</p>}
                  {prospect.historiqueMails?.length > 0 && <p><span className="font-semibold">Mails précédents :</span> {prospect.historiqueMails.length}</p>}
                </div>
              </div>

              <button onClick={handleGenerer} disabled={generation}
                className="w-full flex items-center justify-center gap-2 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors">
                {generation ? <><RefreshCw size={18} className="animate-spin" /> SENTINELLE rédige...</> : <><Sparkles size={18} /> Générer le mail</>}
              </button>

              {erreur && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{erreur}</div>}
            </>
          )}

          {resultat && (
            <>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Objet</p>
                <p className="text-sm font-semibold text-gray-800 bg-gray-50 rounded-lg px-3 py-2">{resultat.objet}</p>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Contenu du mail — modifiable</p>
                <textarea
                  value={contenuEdite}
                  onChange={(e) => setContenuEdite(e.target.value)}
                  className="w-full h-64 px-3 py-2 border rounded-lg text-sm text-gray-800 focus:ring-2 focus:ring-blue-500 resize-none leading-relaxed"
                />
              </div>

              {/* Rappel PJ — toujours visible quand le mail est prêt */}
              <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                <AlertTriangle size={18} className="text-amber-600 flex-shrink-0" />
                <p className="text-sm text-amber-800 font-semibold">N'oubliez pas de joindre le PDF de presentation manuellement dans Gmail.</p>
              </div>

              {envoiOk ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                    <AlertTriangle size={18} className="text-amber-600 flex-shrink-0" />
                    <p className="text-sm text-amber-800 font-semibold">N'oubliez pas de joindre le PDF de présentation manuellement dans Gmail.</p>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
                    <Check size={24} className="text-emerald-600 mx-auto mb-2" />
                    <p className="text-sm font-semibold text-emerald-700">Gmail ouvert — mail enregistré comme envoyé.</p>
                    <button onClick={onFermer} className="mt-3 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors">Fermer</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex gap-2">
                    <button onClick={handleOuvrirGmail} disabled={!prospect.contactEmail}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm">
                      <ExternalLink size={16} /> Ouvrir dans Gmail
                    </button>
                    <button onClick={handleEnregistrer}
                      className="flex items-center justify-center gap-2 py-2.5 px-4 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors text-sm">
                      <Check size={16} /> Brouillon
                    </button>
                  </div>
                  <button onClick={() => { const texte = `Objet : ${resultat.objet}\n\n${resultat.contenuTexte}`; navigator.clipboard.writeText(texte).then(() => { setCopie(true); setTimeout(() => setCopie(false), 2000); }); }}
                    className="w-full flex items-center justify-center gap-2 py-2 text-gray-500 hover:text-gray-700 text-sm transition-colors">
                    <Copy size={14} /> {copie ? 'Copié !' : 'Copier le texte'}
                  </button>
                  <button onClick={() => { setResultat(null); setErreur(''); }}
                    className="w-full flex items-center justify-center gap-2 py-2 text-gray-400 hover:text-gray-600 text-sm transition-colors">
                    <RotateCcw size={14} /> Régénérer
                  </button>
                  {erreur && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{erreur}</div>}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// ONGLET MESSAGERIE
// ============================================


interface OngletMessagerieProps {
  prospects: Prospect[];
  responsables: InfoResponsable[];
  onOuvrirProspect: (prospect: Prospect) => void;
  onGenererMail: (prospect: Prospect) => void;
  onGmailOuvert?: (prospect: Prospect) => void;
  onSupprime?: () => void;
}

const OngletMessagerie: React.FC<OngletMessagerieProps> = ({
  prospects,
  responsables,
  onOuvrirProspect,
  onGenererMail,
  onGmailOuvert,
  onSupprime,
}) => {
  const [filtreMail, setFiltreMail] = useState<'tous' | 'brouillons' | 'envoyes'>('tous');
  const [filtreProspect, setFiltreProspect] = useState<string>('tous');
  const [mailOuvert, setMailOuvert] = useState<{ mail: HistoriqueMailProspect; prospect: Prospect } | null>(null);
  const [envoiOk, setEnvoiOk] = useState(false);
  const [marquerEnCours, setMarquerEnCours] = useState(false);
  const [confirmSupp, setConfirmSupp] = useState(false);
  const [suppressionEnCours, setSuppressionEnCours] = useState(false);
  const [texteEdite, setTexteEdite] = useState<string>('');
  const [modeEdition, setModeEdition] = useState(false);

  // Collecter tous les mails de tous les prospects
  const tousLesMails: Array<{
    mail: HistoriqueMailProspect;
    prospect: Prospect;
  }> = [];

  prospects.forEach((prospect) => {
    (prospect.historiqueMails || []).forEach((mail) => {
      tousLesMails.push({ mail, prospect });
    });
  });

  // Tri anti-chrono (plus récent en premier)
  tousLesMails.sort((a, b) => {
    try {
      const dateA = a.mail.date?.toDate?.()?.getTime() || 0;
      const dateB = b.mail.date?.toDate?.()?.getTime() || 0;
      return dateB - dateA;
    } catch { return 0; }
  });

  // Filtrage
  const mailsFiltres = tousLesMails.filter(({ mail, prospect }) => {
    if (filtreMail === 'brouillons' && mail.envoye) return false;
    if (filtreMail === 'envoyes' && !mail.envoye) return false;
    if (filtreProspect !== 'tous' && prospect.id !== filtreProspect) return false;
    return true;
  });

  const nbBrouillons = tousLesMails.filter((m) => !m.mail.envoye).length;
  const nbEnvoyes = tousLesMails.filter((m) => m.mail.envoye).length;

  const formatDate = (ts?: Timestamp | null): string => {
    if (!ts) return '—';
    try {
      return ts.toDate().toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return '—'; }
  };

  const handleOuvrirMail = (mail: HistoriqueMailProspect, prospect: Prospect) => {
    setMailOuvert({ mail, prospect });
    setTexteEdite(mail.apercu || '');
    setModeEdition(false);
    setEnvoiOk(false);
    setConfirmSupp(false);
  };

  const handleFermerModal = () => {
    setMailOuvert(null);
    setEnvoiOk(false);
    setConfirmSupp(false);
    setModeEdition(false);
    setTexteEdite('');
  };

  const handleOuvrirGmail = async () => {
    if (!mailOuvert || !mailOuvert.prospect.contactEmail) return;
    const to = encodeURIComponent(mailOuvert.prospect.contactEmail);
    const subject = encodeURIComponent(mailOuvert.mail.objet);
    const corps = texteEdite || mailOuvert.mail.apercu || '';
    const body = encodeURIComponent(corps);
    window.open(`https://mail.google.com/mail/?view=cm&to=${to}&su=${subject}&body=${body}`, '_blank');
    // Marquer envoyé en Firestore
    try {
      await marquerMailEnvoye(mailOuvert.prospect.id, mailOuvert.mail.id);
    } catch (err) {
      console.error('[CRM] Erreur marquer envoyé après Gmail:', err);
    }
    setEnvoiOk(true);
    if (onGmailOuvert && mailOuvert.prospect.statut === 'contacte') {
      onGmailOuvert(mailOuvert.prospect);
    }
  };

  const handleMarquerEnvoye = async () => {
    if (!mailOuvert) return;
    setMarquerEnCours(true);
    try {
      await marquerMailEnvoye(mailOuvert.prospect.id, mailOuvert.mail.id);
      setEnvoiOk(true);
    } catch (err) {
      console.error('[CRM] Erreur marquer envoyé:', err);
    } finally {
      setMarquerEnCours(false);
    }
  };

  const handleSupprimerMail = async () => {
    if (!mailOuvert) return;
    setSuppressionEnCours(true);
    try {
      await supprimerMailHistorique(mailOuvert.prospect.id, mailOuvert.mail.id);
      handleFermerModal();
      onSupprime?.();
    } catch (err) {
      console.error('[CRM] Erreur suppression mail:', err);
      setConfirmSupp(false);
    } finally {
      setSuppressionEnCours(false);
    }
  };

  if (tousLesMails.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl shadow-sm">
        <Inbox size={48} className="text-gray-300 mb-4" />
        <h3 className="text-lg font-bold text-gray-700 mb-2">Aucun mail pour l'instant</h3>
        <p className="text-sm text-gray-500 text-center max-w-sm">
          Ouvre un prospect depuis le Pipeline, puis utilise SENTINELLE pour générer un premier mail de prospection.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-3 bg-white p-3 rounded-xl shadow-sm">
        {/* Filtre type */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
          {([
            { value: 'tous', label: `Tous (${tousLesMails.length})` },
            { value: 'brouillons', label: `Brouillons (${nbBrouillons})` },
            { value: 'envoyes', label: `Envoyés (${nbEnvoyes})` },
          ] as const).map((f) => (
            <button key={f.value} onClick={() => setFiltreMail(f.value)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${filtreMail === f.value ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Filtre prospect */}
        <select value={filtreProspect} onChange={(e) => setFiltreProspect(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 text-gray-700 bg-white">
          <option value="tous">Tous les prospects</option>
          {prospects.filter((p) => (p.historiqueMails?.length || 0) > 0).map((p) => (
            <option key={p.id} value={p.id}>{p.nom}</option>
          ))}
        </select>
      </div>

      {/* Liste mails */}
      <div className="space-y-2">
        {mailsFiltres.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm">
            <p className="text-sm text-gray-400">Aucun mail correspondant aux filtres</p>
          </div>
        ) : (
          mailsFiltres.map(({ mail, prospect }, index) => (
            <div key={`${prospect.id}-${mail.id}-${index}`}
              onClick={() => handleOuvrirMail(mail, prospect)}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:border-emerald-300 hover:shadow-md transition-all cursor-pointer">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  {/* Prospect + template */}
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${mail.envoye ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                      {mail.envoye ? 'Envoyé' : 'Brouillon'}
                    </span>
                    <span className="text-xs text-blue-600 font-semibold">{LABELS_TYPE_TEMPLATE[mail.template] || mail.template}</span>
                  </div>

                  {/* Objet */}
                  <p className="font-semibold text-gray-800 text-sm truncate">{mail.objet}</p>

                  {/* Prospect + destinataire */}
                  <div className="flex items-center gap-3 mt-1">
                    <button onClick={(e) => { e.stopPropagation(); onOuvrirProspect(prospect); }}
                      className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold flex items-center gap-1">
                      {ICONES_TYPE_PROSPECT[prospect.typeProspect]}
                      {prospect.nom}
                    </button>
                    {mail.destinataire && (
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Mail size={10} /> {mail.destinataire}
                      </span>
                    )}
                  </div>

                  {/* Aperçu */}
                  {mail.apercu && (
                    <p className="text-xs text-gray-500 mt-1.5 line-clamp-2">{mail.apercu}</p>
                  )}
                </div>

                {/* Méta droite */}
                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  <span className="text-[10px] text-gray-400">{formatDate(mail.date)}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${getCouleurResponsable(mail.auteur, responsables)}`}>
                    {getLabelResponsable(mail.auteur, responsables).split(' ')[0]}
                  </span>
                  {!mail.envoye && prospect.contactEmail ? (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        const to = encodeURIComponent(prospect.contactEmail!);
                        const subject = encodeURIComponent(mail.objet);
                        const body = encodeURIComponent(mail.apercu || '');
                        window.open(`https://mail.google.com/mail/?view=cm&to=${to}&su=${subject}&body=${body}`, '_blank');
                        try { await marquerMailEnvoye(prospect.id, mail.id); } catch { /* non bloquant */ }
                      }}
                      className="flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 font-semibold transition-colors">
                      <ExternalLink size={10} /> Gmail
                    </button>
                  ) : (
                    <span className="text-[10px] text-gray-400 flex items-center gap-1">
                      <Eye size={10} /> Ouvrir
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal détail mail */}
      {mailOuvert && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-xl">
              <div className="flex items-center gap-2 text-white">
                <Mail size={18} />
                <div>
                  <h2 className="font-bold text-sm leading-tight">{mailOuvert.mail.objet}</h2>
                  <p className="text-blue-200 text-xs mt-0.5">{mailOuvert.prospect.nom} — {mailOuvert.mail.destinataire}</p>
                </div>
              </div>
              <button onClick={handleFermerModal} className="p-1 text-white/80 hover:text-white rounded"><X size={20} /></button>
            </div>

            {/* Métadonnées */}
            <div className="flex items-center gap-3 px-4 py-2.5 border-b bg-gray-50">
              <span className={`text-xs px-2 py-0.5 rounded font-semibold ${mailOuvert.mail.envoye ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                {mailOuvert.mail.envoye ? 'Envoyé' : 'Brouillon'}
              </span>
              <span className="text-xs text-blue-600 font-semibold">{LABELS_TYPE_TEMPLATE[mailOuvert.mail.template]}</span>
              <span className="text-xs text-gray-400">{formatDate(mailOuvert.mail.date)}</span>
              <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ml-auto ${getCouleurResponsable(mailOuvert.mail.auteur, responsables)}`}>
                {getLabelResponsable(mailOuvert.mail.auteur, responsables).split(' ')[0]}
              </span>
            </div>

            {/* Contenu */}
            <div className="flex-1 overflow-y-auto p-4">
              {!mailOuvert.mail.envoye && (
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-400">Texte envoyé dans Gmail</p>
                  <button
                    onClick={() => setModeEdition((v) => !v)}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-semibold transition-colors">
                    <Edit3 size={12} /> {modeEdition ? 'Aperçu HTML' : 'Modifier le texte'}
                  </button>
                </div>
              )}
              {!mailOuvert.mail.envoye && modeEdition ? (
                <textarea
                  value={texteEdite}
                  onChange={(e) => setTexteEdite(e.target.value)}
                  className="w-full h-72 px-3 py-2 border rounded-lg text-sm text-gray-800 focus:ring-2 focus:ring-blue-500 resize-none font-mono leading-relaxed"
                  placeholder="Contenu du mail..."
                />
              ) : mailOuvert.mail.contenuHtml ? (
                <div className="border rounded-lg overflow-hidden bg-white" style={{ minHeight: '300px', maxHeight: '400px', overflowY: 'auto' }}>
                  <iframe
                    srcDoc={mailOuvert.mail.contenuHtml}
                    title="Contenu du mail"
                    className="w-full border-0"
                    style={{ minHeight: '380px' }}
                  />
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-4">
                  {mailOuvert.mail.apercu ? (
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{mailOuvert.mail.apercu}</p>
                  ) : (
                    <p className="text-sm text-gray-400 italic text-center py-6">
                      Contenu non disponible — ce brouillon a été généré avant la mise à jour.<br />
                      Génère un nouveau mail depuis la fiche prospect.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="p-4 border-t bg-gray-50 rounded-b-xl">
              {/* Rappel PJ — visible dès qu'un brouillon est ouvert */}
              {!mailOuvert.mail.envoye && !envoiOk && (
                <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-2">
                  <AlertTriangle size={18} className="text-amber-600 flex-shrink-0" />
                  <p className="text-sm text-amber-800 font-semibold">N'oubliez pas de joindre le PDF de presentation manuellement dans Gmail.</p>
                </div>
              )}

              {envoiOk ? (
                <div className="flex items-center justify-center gap-2 py-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <Check size={18} className="text-emerald-600" />
                  <p className="text-sm font-semibold text-emerald-700">Gmail ouvert — mail marque comme envoye</p>
                </div>
              ) : mailOuvert.mail.envoye ? (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500">Ce mail a déjà été envoyé.</p>
                  <button onClick={handleFermerModal}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-300 transition-colors">
                    Fermer
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    {mailOuvert.prospect.contactEmail && (
                      <button onClick={handleOuvrirGmail}
                        className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors text-sm">
                        <ExternalLink size={16} /> Ouvrir dans Gmail
                      </button>
                    )}
                    <button onClick={handleMarquerEnvoye} disabled={marquerEnCours}
                      className="flex items-center justify-center gap-2 py-2.5 px-4 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 disabled:opacity-50 transition-colors text-sm">
                      {marquerEnCours ? <RefreshCw size={16} className="animate-spin" /> : <Check size={16} />}
                      Marquer envoyé
                    </button>
                    <button onClick={() => { handleFermerModal(); onGenererMail(mailOuvert.prospect); }}
                      className="flex items-center justify-center gap-2 py-2.5 px-4 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg font-semibold hover:bg-amber-100 transition-colors text-sm">
                      <Sparkles size={16} /> Régénérer
                    </button>
                  </div>
                </div>
              )}

              {/* Suppression — double confirmation */}
              <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-end">
                {confirmSupp ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-red-600 font-semibold">Supprimer ce mail ?</span>
                    <button onClick={handleSupprimerMail} disabled={suppressionEnCours}
                      className="px-3 py-1.5 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors">
                      {suppressionEnCours ? 'Suppression...' : 'Confirmer'}
                    </button>
                    <button onClick={() => setConfirmSupp(false)}
                      className="px-3 py-1.5 bg-gray-200 text-gray-600 text-xs rounded-lg hover:bg-gray-300 transition-colors">
                      Annuler
                    </button>
                  </div>
                ) : (
                  <button onClick={() => setConfirmSupp(true)}
                    className="flex items-center gap-1 text-xs text-red-400 hover:text-red-600 transition-colors">
                    <Trash2 size={13} /> Supprimer ce mail
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// ONGLET PROSPECTS (liste)
// ============================================

interface OngletProspectsProps {
  prospects: Prospect[];
  responsables: InfoResponsable[];
  onOuvrirDetail: (prospect: Prospect) => void;
  onCreer: () => void;
}

const ORDRE_SEGMENT: SegmentProspect[] = ['ambassadeur', 'client_direct', 'recommandeur'];

const OngletProspects: React.FC<OngletProspectsProps> = ({
  prospects,
  responsables,
  onOuvrirDetail,
  onCreer,
}) => {
  const [recherche, setRecherche] = useState('');
  const [filtreStatut, setFiltreStatut] = useState<StatutProspect | 'tous'>('tous');

  const prospectsFiltres = prospects.filter((p) => {
    if (filtreStatut !== 'tous' && p.statut !== filtreStatut) return false;
    if (recherche) {
      const s = recherche.toLowerCase();
      return p.nom.toLowerCase().includes(s) ||
        (p.contactNom || '').toLowerCase().includes(s) ||
        (p.contactEmail || '').toLowerCase().includes(s);
    }
    return true;
  });

  // Grouper par segment dans l'ordre : ambassadeur → client_direct → recommandeur
  const groupesParSegment = ORDRE_SEGMENT.map((segment) => ({
    segment,
    prospects: prospectsFiltres.filter((p) => p.segment === segment),
  })).filter((g) => g.prospects.length > 0);

  // Prospects sans segment (fallback)
  const sansSeg = prospectsFiltres.filter((p) => !p.segment || !ORDRE_SEGMENT.includes(p.segment as SegmentProspect));

  const formatDate = (ts?: Timestamp | null): string => {
    if (!ts) return '—';
    try { return ts.toDate().toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return '—'; }
  };

  const SEPARATEUR_COULEURS: Record<SegmentProspect, string> = {
    ambassadeur:   'bg-emerald-50 border-emerald-200 text-emerald-700',
    client_direct: 'bg-purple-50 border-purple-200 text-purple-700',
    recommandeur:  'bg-blue-50 border-blue-200 text-blue-700',
  };

  const renderLignes = (liste: Prospect[]) =>
    liste.map((prospect) => (
      <tr key={prospect.id} onClick={() => onOuvrirDetail(prospect)}
        className="hover:bg-gray-50 cursor-pointer transition-colors group">
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            {ICONES_TYPE_PROSPECT[prospect.typeProspect]}
            <span className="font-semibold text-sm text-gray-800">{prospect.nom}</span>
          </div>
          {prospect.tailleStructure && (
            <p className="text-xs text-gray-400 mt-0.5 ml-5">{prospect.tailleStructure}</p>
          )}
        </td>
        <td className="px-4 py-3">
          <p className="text-sm text-gray-700">{prospect.contactNom || '—'}</p>
          {prospect.contactEmail && <p className="text-xs text-gray-400">{prospect.contactEmail}</p>}
        </td>
        <td className="px-4 py-3">
          <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-gray-100 text-gray-600">
            {LABELS_STATUT_PROSPECT[prospect.statut]}
          </span>
        </td>
        <td className="px-4 py-3">
          {prospect.segment ? (
            <span className={`text-xs px-2 py-0.5 rounded font-bold border ${COULEURS_SEGMENT_PROSPECT[prospect.segment]}`}>
              {LABELS_SEGMENT_PROSPECT[prospect.segment as SegmentProspect]}
            </span>
          ) : '—'}
        </td>
        <td className="px-4 py-3">
          <span className="text-sm text-gray-600">
            {(prospect.historiqueMails?.length || 0) > 0 ? (
              <span className="flex items-center gap-1">
                <Mail size={12} className="text-blue-400" />
                {prospect.historiqueMails!.length}
              </span>
            ) : '—'}
          </span>
        </td>
        <td className="px-4 py-3">
          <span className={`text-xs px-2 py-0.5 rounded font-semibold ${getCouleurResponsable(prospect.responsable, responsables)}`}>
            {getLabelResponsable(prospect.responsable, responsables).split(' ')[0]}
          </span>
        </td>
        <td className="px-4 py-3 text-xs text-gray-400">{formatDate(prospect.createdAt)}</td>
        <td className="px-4 py-3">
          <ChevronRight size={16} className="text-gray-300 group-hover:text-emerald-500 transition-colors" />
        </td>
      </tr>
    ));

  return (
    <div className="space-y-4">
      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-3 bg-white p-3 rounded-xl shadow-sm">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={recherche} onChange={(e) => setRecherche(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
            placeholder="Rechercher..." />
        </div>
        <select value={filtreStatut} onChange={(e) => setFiltreStatut(e.target.value as StatutProspect | 'tous')}
          className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 bg-white text-gray-700">
          <option value="tous">Tous les statuts</option>
          {Object.entries(LABELS_STATUT_PROSPECT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button onClick={onCreer}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold text-sm transition-colors">
          <Plus size={16} /> Nouveau
        </button>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {prospectsFiltres.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-sm text-gray-400">Aucun prospect correspondant</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Prospect</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Contact</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Statut</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Segment</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Mails</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Responsable</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Créé le</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {groupesParSegment.map(({ segment, prospects: groupe }) => (
                <React.Fragment key={segment}>
                  {/* Séparateur de segment */}
                  <tr>
                    <td colSpan={8} className="px-4 py-2">
                      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-bold w-fit ${SEPARATEUR_COULEURS[segment]}`}>
                        {LABELS_SEGMENT_PROSPECT[segment]}
                        <span className="font-normal opacity-70">— {groupe.length} prospect{groupe.length > 1 ? 's' : ''}</span>
                      </div>
                    </td>
                  </tr>
                  {renderLignes(groupe)}
                </React.Fragment>
              ))}
              {/* Prospects sans segment */}
              {sansSeg.length > 0 && (
                <React.Fragment>
                  <tr>
                    <td colSpan={8} className="px-4 py-2">
                      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-bold w-fit bg-gray-50 border-gray-200 text-gray-500">
                        Non classés
                        <span className="font-normal opacity-70">— {sansSeg.length} prospect{sansSeg.length > 1 ? 's' : ''}</span>
                      </div>
                    </td>
                  </tr>
                  {renderLignes(sansSeg)}
                </React.Fragment>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ============================================
// ONGLET ACTIONS DU JOUR
// Session 207 : Vue opérationnelle — que faire maintenant ?
// ============================================

interface OngletActionsDuJourProps {
  prospects: Prospect[];
  responsables: InfoResponsable[];
  filtreResponsable: string;
  onOuvrirProspect: (p: Prospect) => void;
}

const OngletActionsDuJour: React.FC<OngletActionsDuJourProps> = ({
  prospects,
  responsables,
  filtreResponsable,
  onOuvrirProspect,
}) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Filtrage par responsable
  const prospectsFiltres = filtreResponsable === 'tous'
    ? prospects
    : prospects.filter((p) => p.responsable === filtreResponsable);

  // Exclure convertis et perdus des sections 1/3/4
  const actifs = prospectsFiltres.filter((p) => p.statut !== 'converti' && p.statut !== 'perdu');

  // ---- Section 1 : Actions planifiées aujourd'hui ou en retard ----
  const actionsAFaire = actifs
    .filter((p) => {
      if (!p.dateProchaineAction) return false;
      const d = p.dateProchaineAction.toDate();
      d.setHours(0, 0, 0, 0);
      return d <= today;
    })
    .sort((a, b) => {
      const da = a.dateProchaineAction!.toDate().getTime();
      const db = b.dateProchaineAction!.toDate().getTime();
      return da - db;
    });

  // ---- Section 2 : Brouillons en attente depuis +48h (tous statuts sauf converti/perdu) ----
  const seuilBrouillon = new Date(today.getTime() - 48 * 60 * 60 * 1000);
  const brouillonsEnAttente: Array<{ prospect: Prospect; mail: HistoriqueMailProspect }> = [];
  prospectsFiltres
    .filter((p) => p.statut !== 'converti' && p.statut !== 'perdu')
    .forEach((p) => {
      (p.historiqueMails || [])
        .filter((m) => !m.envoye && (!m.type || m.type === 'brouillon'))
        .forEach((m) => {
          const d = m.date?.toDate?.() || new Date(m.date as unknown as string);
          if (d < seuilBrouillon) {
            brouillonsEnAttente.push({ prospect: p, mail: m });
          }
        });
    });
  brouillonsEnAttente.sort((a, b) => {
    const da = a.mail.date?.toDate?.().getTime() || 0;
    const db = b.mail.date?.toDate?.().getTime() || 0;
    return da - db;
  });

  // ---- Section 3 : Inactivité +14j sans contact ----
  const seuilInactivite = new Date(today.getTime() - 14 * 24 * 60 * 60 * 1000);
  const inactifs = actifs.filter((p) => {
    const derniere = getDerniereActivite(p);
    return derniere < seuilInactivite;
  }).sort((a, b) => {
    return getDerniereActivite(a).getTime() - getDerniereActivite(b).getTime();
  });

  // ---- Section 4 : Réponses reçues sans relance générée ----
  const reponsesEnAttente = actifs.filter((p) => hasReponseEnAttente(p));

  const total = actionsAFaire.length + brouillonsEnAttente.length + inactifs.length + reponsesEnAttente.length;

  const formatDateRelative = (d: Date): string => {
    const diff = Math.floor((today.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
    if (diff === 0) return "aujourd'hui";
    if (diff === 1) return 'hier';
    return `il y a ${diff}j`;
  };

  const formatDateAction = (ts: Timestamp): string => {
    const d = ts.toDate();
    const diff = Math.floor((today.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
    if (diff === 0) return "aujourd'hui";
    if (diff < 0) return `dans ${Math.abs(diff)}j`;
    return `retard ${diff}j`;
  };

  if (total === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mb-4">
          <CheckCircle2 size={32} className="text-emerald-500" />
        </div>
        <h3 className="text-lg font-bold text-gray-800 mb-1">Rien en attente</h3>
        <p className="text-sm text-gray-500">Bonne journée — tout est à jour !</p>
      </div>
    );
  }

  const SectionHeader: React.FC<{
    icon: React.ReactNode;
    titre: string;
    count: number;
    couleur: string;
    bgCouleur: string;
  }> = ({ icon, titre, count, couleur, bgCouleur }) => (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${bgCouleur} mb-2`}>
      <div className={`${couleur}`}>{icon}</div>
      <span className={`font-bold text-sm ${couleur}`}>{titre}</span>
      <span className={`ml-auto text-xs font-bold px-2 py-0.5 rounded-full ${couleur} bg-white/70`}>
        {count}
      </span>
    </div>
  );

  const LigneProspect: React.FC<{
    prospect: Prospect;
    meta: string;
    metaCouleur: string;
    sousTitre?: string;
  }> = ({ prospect, meta, metaCouleur, sousTitre }) => (
    <button
      onClick={() => onOuvrirProspect(prospect)}
      className="w-full flex items-center gap-3 px-4 py-3 bg-white rounded-lg border border-gray-200 hover:border-emerald-300 hover:shadow-sm transition-all text-left group"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm text-gray-800 truncate group-hover:text-emerald-700 transition-colors">
            {prospect.nom}
          </span>
          <span className="text-[10px] text-gray-400 shrink-0">
            {LABELS_STATUT_PROSPECT[prospect.statut]}
          </span>
        </div>
        {sousTitre && (
          <p className="text-xs text-gray-500 truncate mt-0.5">{sousTitre}</p>
        )}
      </div>
      <span className={`text-xs font-semibold shrink-0 ${metaCouleur}`}>{meta}</span>
      <ChevronRight size={14} className="text-gray-300 group-hover:text-emerald-500 transition-colors shrink-0" />
    </button>
  );

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Résumé */}
      <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
        <Zap size={16} className="text-amber-500 shrink-0" />
        <p className="text-sm font-semibold text-amber-800">
          {total} action{total > 1 ? 's' : ''} en attente
          {filtreResponsable !== 'tous' && (
            <span className="font-normal text-amber-600 ml-1">
              — {responsables.find((r) => r.identifiant === filtreResponsable)?.nomComplet.split(' ')[0] || filtreResponsable}
            </span>
          )}
        </p>
      </div>

      {/* Section 1 — Actions planifiées dues */}
      {actionsAFaire.length > 0 && (
        <div>
          <SectionHeader
            icon={<CalendarClock size={16} />}
            titre="Actions planifiées — dues"
            count={actionsAFaire.length}
            couleur="text-red-700"
            bgCouleur="bg-red-50 border-red-200"
          />
          <div className="space-y-2">
            {actionsAFaire.map((p) => (
              <LigneProspect
                key={p.id}
                prospect={p}
                meta={formatDateAction(p.dateProchaineAction!)}
                metaCouleur="text-red-600"
                sousTitre={p.prochaineAction}
              />
            ))}
          </div>
        </div>
      )}

      {/* Section 2 — Brouillons en attente */}
      {brouillonsEnAttente.length > 0 && (
        <div>
          <SectionHeader
            icon={<Clock size={16} />}
            titre="Brouillons non envoyés — +48h"
            count={brouillonsEnAttente.length}
            couleur="text-amber-700"
            bgCouleur="bg-amber-50 border-amber-200"
          />
          <div className="space-y-2">
            {brouillonsEnAttente.map(({ prospect, mail }) => (
              <LigneProspect
                key={`${prospect.id}-${mail.id}`}
                prospect={prospect}
                meta={formatDateRelative(mail.date?.toDate?.() || new Date())}
                metaCouleur="text-amber-600"
                sousTitre={mail.objet}
              />
            ))}
          </div>
        </div>
      )}

      {/* Section 3 — Inactivité +14j */}
      {inactifs.length > 0 && (
        <div>
          <SectionHeader
            icon={<AlertCircle size={16} />}
            titre="Sans contact depuis +14 jours"
            count={inactifs.length}
            couleur="text-orange-700"
            bgCouleur="bg-orange-50 border-orange-200"
          />
          <div className="space-y-2">
            {inactifs.map((p) => (
              <LigneProspect
                key={p.id}
                prospect={p}
                meta={formatDateRelative(getDerniereActivite(p))}
                metaCouleur="text-orange-600"
                sousTitre={p.prochaineAction || undefined}
              />
            ))}
          </div>
        </div>
      )}

      {/* Section 4 — Réponses sans relance */}
      {reponsesEnAttente.length > 0 && (
        <div>
          <SectionHeader
            icon={<RotateCcw size={16} />}
            titre="Réponses reçues — relance non générée"
            count={reponsesEnAttente.length}
            couleur="text-emerald-700"
            bgCouleur="bg-emerald-50 border-emerald-200"
          />
          <div className="space-y-2">
            {reponsesEnAttente.map((p) => (
              <LigneProspect
                key={p.id}
                prospect={p}
                meta="Réponse en attente"
                metaCouleur="text-emerald-600"
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// ONGLET TABLEAU DE BORD
// ============================================

interface OngletTableauBordProps {
  prospects: Prospect[];
  responsables: InfoResponsable[];
}

const OngletTableauBord: React.FC<OngletTableauBordProps> = ({ prospects, responsables }) => {
  const totalProspects = prospects.length;
  const nbConvertis = prospects.filter((p) => p.statut === 'converti').length;
  const nbPerdus = prospects.filter((p) => p.statut === 'perdu').length;
  const nbEnCours = totalProspects - nbConvertis - nbPerdus;

  const tousLesMails = prospects.flatMap((p) => (p.historiqueMails || []).map((m) => ({ mail: m, prospect: p })));
  const brouillons = tousLesMails.filter((m) => !m.mail.envoye);
  const envoyes = tousLesMails.filter((m) => m.mail.envoye);

  // Prochaines actions (dateProchaineAction définie, non converti, non perdu)
  const today = new Date();
  const prochainesActions = prospects
    .filter((p) => p.dateProchaineAction && p.statut !== 'converti' && p.statut !== 'perdu')
    .sort((a, b) => {
      try { return a.dateProchaineAction!.toDate().getTime() - b.dateProchaineAction!.toDate().getTime(); }
      catch { return 0; }
    })
    .slice(0, 5);

  // Prospects sans email
  const sansEmail = prospects.filter((p) => !p.contactEmail && p.statut !== 'converti' && p.statut !== 'perdu');

  const formatDate = (ts?: Timestamp | null): string => {
    if (!ts) return '—';
    try {
      const d = ts.toDate();
      const diff = Math.ceil((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      if (diff === 0) return "Aujourd'hui";
      if (diff === 1) return 'Demain';
      if (diff < 0) return `Il y a ${Math.abs(diff)}j`;
      return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
    } catch { return '—'; }
  };

  return (
    <div className="space-y-6">
      {/* Chiffres clés */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total prospects', value: totalProspects, icon: <Target size={20} className="text-gray-500" />, color: 'bg-gray-50 border-gray-200' },
          { label: 'En cours', value: nbEnCours, icon: <TrendingUp size={20} className="text-blue-500" />, color: 'bg-blue-50 border-blue-200' },
          { label: 'Convertis', value: nbConvertis, icon: <CheckCircle2 size={20} className="text-emerald-500" />, color: 'bg-emerald-50 border-emerald-200' },
          { label: 'Mails envoyés', value: envoyes.length, icon: <Send size={20} className="text-purple-500" />, color: 'bg-purple-50 border-purple-200' },
        ].map((stat) => (
          <div key={stat.label} className={`${stat.color} border rounded-xl p-4`}>
            <div className="flex items-center justify-between mb-2">
              {stat.icon}
              <span className="text-2xl font-bold text-gray-800">{stat.value}</span>
            </div>
            <p className="text-xs text-gray-500 font-medium">{stat.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Prochaines actions */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center gap-2 mb-4">
            <CalendarClock size={18} className="text-amber-500" />
            <h3 className="font-bold text-gray-800">Prochaines actions</h3>
          </div>
          {prochainesActions.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">Aucune action planifiée</p>
          ) : (
            <div className="space-y-2">
              {prochainesActions.map((p) => {
                const diff = p.dateProchaineAction
                  ? Math.ceil((p.dateProchaineAction.toDate().getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
                  : null;
                const enRetard = diff !== null && diff < 0;
                return (
                  <div key={p.id} className={`flex items-center justify-between p-2.5 rounded-lg ${enRetard ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">{p.nom}</p>
                      <p className="text-xs text-gray-500">{p.prochaineAction}</p>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded ${enRetard ? 'text-red-600' : 'text-amber-600'}`}>
                      {formatDate(p.dateProchaineAction)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Alertes */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle size={18} className="text-red-500" />
            <h3 className="font-bold text-gray-800">Alertes</h3>
          </div>
          <div className="space-y-3">
            {brouillons.length > 0 && (
              <div className="flex items-center justify-between p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <Clock size={16} className="text-amber-500" />
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{brouillons.length} brouillon{brouillons.length > 1 ? 's' : ''} en attente</p>
                    <p className="text-xs text-gray-500">Mails générés mais non envoyés</p>
                  </div>
                </div>
              </div>
            )}
            {sansEmail.length > 0 && (
              <div className="flex items-center justify-between p-2.5 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <Mail size={16} className="text-red-500" />
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{sansEmail.length} prospect{sansEmail.length > 1 ? 's' : ''} sans email</p>
                    <p className="text-xs text-gray-500">SENTINELLE ne peut pas agir</p>
                  </div>
                </div>
              </div>
            )}
            {brouillons.length === 0 && sansEmail.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-6">Aucune alerte — tout est en ordre</p>
            )}
          </div>

          {/* Répartition par statut */}
          <div className="mt-4 border-t pt-4">
            <p className="text-xs font-semibold text-gray-400 uppercase mb-3">Répartition pipeline</p>
            <div className="space-y-2">
              {COLONNES_KANBAN.map((statut) => {
                const count = prospects.filter((p) => p.statut === statut).length;
                const pct = totalProspects > 0 ? Math.round((count / totalProspects) * 100) : 0;
                return (
                  <div key={statut}>
                    <div className="flex justify-between text-xs mb-0.5">
                      <span className="text-gray-600">{LABELS_STATUT_PROSPECT[statut]}</span>
                      <span className="font-semibold text-gray-700">{count}</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// Prompt prospection SENTINELLE V3 — téléchargeable depuis le guide CRM
const PROMPT_ENRICHISSEMENT_PROSPECTS = `# PROMPT SYSTÈME — SENTINELLE Prospection Copro Protect
# DOM TECH & SERVICES
# Version 3.0 — 10 mars 2026
# Usage : coller en system prompt d'une session Claude.ai standalone (hors projet)

---

## IDENTITÉ ET MISSION

Tu es **SENTINELLE**, l'assistant de prospection de **Copro Protect** — une plateforme SaaS française qui protège les copropriétaires et les conseils syndicaux face aux syndics professionnels.

Chaque session avec toi a un objectif unique : **identifier un prospect, enrichir sa fiche, produire deux livrables HTML**.

Tu pilotes la conversation de A à Z. Tu ne réponds pas passivement — tu **ouvres**, tu **guides**, tu **produis**.

---

## CONTEXTE PRODUIT — CE QUE TU DOIS SAVOIR

**Copro Protect** propose deux offres :

- **VIGIE** (5,90 €/mois ou 59 €/an — Fondateur beta) — pour le copropriétaire solo : analyse de documents, génération de courriers SCUD, suivi de dossiers et signalements.
- **CS SENTINELLE / BOUCLIER / FORTERESSE** (23,90 à 59,90 €/mois) — pour les conseils syndicaux : contrôle du syndic, analyse documentaire IA, courriers juridiques SCUD.

**Le différenciateur central** : SENTINELLE, une IA spécialisée copropriété qui analyse les documents (PV d'AG, appels de fonds, contrats syndic, devis travaux), détecte les anomalies et génère des courriers juridiquement solides.

**Les trois segments de prospects :**
- **Ambassadeur** : association ou fédération (ARC, UNARC, ANCC, UNPI, CLCV, AFCopro, ADACC…) — recommande Copro Protect à ses adhérents, touche une commission. Impact massif.
- **Client direct** : copropriétaire ou CS qui souscrit pour lui-même.
- **Recommandeur** : prescripteur ponctuel (notaire, avocat, gestionnaire indépendant).

**Problématiques courantes :**
Syndic opaque, charges excessives, travaux mal gérés, AG non conformes, absence de communication, litiges copropriétaires, recouvrement, carnet d'entretien non tenu.

---

## RÈGLE ABSOLUE : UNE SEULE QUESTION À LA FOIS

Tu ne poses jamais deux questions simultanément. Tu attends la réponse, tu accuses réception en une phrase courte, puis tu poses la suivante. Si une réponse est incomplète, tu reformules. Si l'utilisateur ne sait pas, tu notes "Non renseigné" et tu passes à la suite.

---

## STRUCTURE DE LA SESSION — 3 PHASES

---

### PHASE 1 — CIBLAGE (ouverture obligatoire)

**Tu ouvres TOUJOURS la session par cette phrase exacte :**

> "Bonjour ! On va identifier ensemble votre prochain prospect Copro Protect. Je vous pose quelques questions pour cibler la meilleure opportunité."

Puis tu enchaînes immédiatement avec Q1.

**Q1 — Type de cible**
> "Quel type de prospect vous intéresse : une association ou fédération de copropriétaires (ambassadeur), un conseil syndical, un copropriétaire individuel, ou un prescripteur (notaire, avocat...) ?"

**Q2 — Zone géographique**
> "Quelle zone géographique — locale (votre région), régionale, ou nationale ?"

**Q3 — Avez-vous déjà une piste ?**
> "Vous avez déjà un nom en tête, ou on cherche ensemble ?"

- Si l'utilisateur a déjà un nom : passer directement en Phase 2.
- Si non : proposer une recherche et identifier le prospect avant de continuer.

**Q4 — Responsable**
> "Ce prospect, c'est Dominique qui le gère ou Alain ?"

---

### PHASE 2 — ENRICHISSEMENT DE LA FICHE

Le workflow fonctionne en deux temps. Tu demandes d'abord :

> "On est avant ou après l'appel avec ce prospect ?"

---

#### CAS A — AVANT L'APPEL

**Q1 — Nom**
> "Quel est le nom complet de la structure ou de la personne ?"

**Q2 — Type**
> "C'est plutôt : une association ou fédération, un conseil syndical, un copropriétaire individuel, ou un prescripteur ?"

**Q3 — Produit visé**
> "Quelle offre semble la plus adaptée — VIGIE, CS SENTINELLE, CS BOUCLIER, CS FORTERESSE, ou c'est encore flou ?"

**Q4 — Segment**
> "Comment tu positionnes ce prospect : ambassadeur, client direct, ou recommandeur ponctuel ?"

**Q5 — Contact**
> "Tu as un contact précis — nom, prénom, fonction ?"

**Q6 — Email**
> "Son email ?"

**Q7 — Téléphone**
> "Son numéro de téléphone ?"

**Q8 — Adresse / Ville**
> "La ville ou l'adresse de la structure ?"

**Q9 — Site web**
> "Tu as le site web ?"

**Q10 — Taille**
> "Une idée de leur taille — adhérents, lots, copropriétés gérées ? Même une estimation."

**Q11 — Source**
> "Comment tu as trouvé ce prospect ?"

**Q12 — Objectif du contact**
> "Quel est l'objectif de cet appel — présenter Copro Protect, proposer un partenariat, décrocher une démo ?"

**Q13 — Prochaine action**
> "La prochaine action concrète, c'est quoi ?"

**Q14 — Notes contextuelles**
> "Un contexte particulier à noter — relation existante, point de vigilance, info utile ?"

- Après Q14 : passer en Phase 3 — générer les deux livrables HTML.

---

#### CAS B — APRÈS L'APPEL

**Q1 — Nom du prospect**
> "C'est pour quel prospect ?"

**Q2 — Problématiques identifiées**
> "Quelles problématiques concrètes il t'a évoquées pendant l'appel ?"
*(Si l'utilisateur hésite : "Tu veux que je te liste les catégories habituelles ?")*

**Q3 — Objectif affiné**
> "L'objectif s'est précisé suite à l'appel ?"

**Q4 — Intérêt perçu**
> "Tu as senti quel niveau d'intérêt — très intéressé, curieux mais prudent, tiède, pas convaincu ?"

**Q5 — Statut pipeline**
> "On fait avancer le statut — il reste 'Identifié', ou il passe à 'Contacté', 'Intéressé', 'Démo' ?"

**Q6 — Prochaine action mise à jour**
> "Quelle est la prochaine action concrète maintenant ?"

**Q7 — Notes de suivi**
> "Des éléments importants à noter — ce qu'il a dit, ce qu'il a demandé, un point de vigilance ?"

- Après Q7 : passer en Phase 3 — générer les deux livrables HTML.

---

### PHASE 3 — GÉNÉRATION DES DEUX LIVRABLES HTML

Dès que la collecte est terminée (avant ou après appel), tu produis **obligatoirement** les deux livrables suivants dans le même message, l'un après l'autre.

Tu annonces :
> "Voilà vos deux livrables. Le premier est la fiche CRM à saisir dans Copro Protect. Le second est la fiche prospect visuelle."

---

#### LIVRABLE 1 — FICHE CRM (HTML structuré pour saisie)

Génère un fichier HTML autonome avec :
- En-tête : nom du prospect, statut pipeline, date
- Tous les champs CRM organisés en sections (Identité, Contact, Données, Prospection, Notes)
- Chaque champ clairement labellisé, valeur en gras
- Les champs vides affichés en italique gris "À compléter"
- Un bouton "Copier tout" pour copier les données dans le presse-papier
- Style : sobre, fonctionnel, lisible, noir/blanc/bleu marine, police système

---

#### LIVRABLE 2 — FICHE PROSPECT VISUELLE (HTML premium)

Génère un fichier HTML autonome avec un design soigné :
- En-tête sombre (bleu marine) avec nom, badge statut pipeline, badge "★ AVANT APPEL" ou "★ APRÈS APPEL", date
- Section Identité : tags colorés pour segment et produit visé
- Section Contact : tous les coordonnées
- Section Données clés : stats en boîtes (nombre d'adhérents, lots, etc.)
- Section Problématiques : hachurée gris clair si avant appel, remplie si après appel
- Section Objectif & Prochaine action : encadré vert avec script d'accroche
- Section Analyse SENTINELLE : bloc sombre avec analyse stratégique 3-5 lignes, leviers (points verts/orange/rouge), ton recommandé
- Section Notes contextuelles
- Footer : Copro Protect / DOM TECH & SERVICES / date
- Boutons : Imprimer, Copier texte
- Typographie : Google Fonts (Syne + DM Sans)
- Responsive mobile

**Dans la section Analyse SENTINELLE tu dois :**
- Expliquer pourquoi ce prospect est pertinent pour Copro Protect
- Identifier le levier principal à actionner lors de l'appel
- Anticiper le frein ou l'objection probable
- Recommander le ton : neutre / enthousiaste / prudent / autorité / pair à pair
- Lister les leviers avec code couleur : vert (atout), orange (vigilance), rouge (risque)

---

## RÈGLES DE COMPORTEMENT

- Tu tutoies l'utilisateur (Dominique ou Alain).
- Tu es concis dans tes questions — pas de longs préambules.
- Tu n'inventes aucune donnée. Si une information manque, tu notes "Non renseigné" ou "À compléter".
- Tu ne remplis les problématiques qu'avec ce que l'utilisateur t'a dit — jamais de projection non confirmée.
- Si l'utilisateur arrive avec des infos déjà connues, tu identifies les champs manquants et ne repose que les questions nécessaires.
- Si l'utilisateur veut modifier un champ après la synthèse, tu régénères immédiatement les deux HTML sans redemander tout le dialogue.
- Si une recherche web est nécessaire pour enrichir les données du prospect (coordonnées, taille, contexte), tu la fais avant de passer en Phase 3.
- Les deux livrables HTML sont toujours générés en fin de session, même si certains champs sont incomplets.

---

## RÉSUMÉ DU FLUX

OUVERTURE
  > PHASE 1 — Ciblage (3-4 questions)
  > [Recherche web si nécessaire]
  > PHASE 2 — Enrichissement (avant ou après appel)
  > PHASE 3 — Génération HTML 1 (fiche CRM) + HTML 2 (fiche visuelle)
  > FIN DE SESSION

L'utilisateur saisit les données dans le CRM Copro Protect.
La fiche vit dans le Kanban (Identifié / Contacté / Intéressé / Démo / Client).
Nouvelle session Claude pour chaque nouvel entretien (workflow "après appel").

---

*SENTINELLE v3.0 — DOM TECH & SERVICES — 10 mars 2026*
`;

// ONGLET GUIDE — Aide complète pour les utilisateurs du CRM
// ============================================

const OngletGuide: React.FC = () => {
  const [sectionOuverte, setSectionOuverte] = useState<string | null>('vision');
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    const chargerPdfUrl = async () => {
      try {
        const snap = await getDoc(doc(db, 'config', 'documents'));
        if (snap.exists()) {
          const data = snap.data();
          if (data.pdfPresentationUrl) setPdfUrl(data.pdfPresentationUrl);
        }
      } catch (err) {
        console.error('[Guide] Erreur chargement URL PDF:', err);
      }
    };
    chargerPdfUrl();
  }, []);

  const toggle = (id: string) => setSectionOuverte((prev) => (prev === id ? null : id));

  const Section: React.FC<{ id: string; titre: string; emoji: string; children: React.ReactNode }> = ({ id, titre, emoji, children }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <button
        onClick={() => toggle(id)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-xl">{emoji}</span>
          <span className="font-bold text-gray-800">{titre}</span>
        </div>
        <ChevronDown
          size={18}
          className={`text-gray-400 transition-transform duration-200 ${sectionOuverte === id ? 'rotate-180' : ''}`}
        />
      </button>
      {sectionOuverte === id && (
        <div className="px-5 pb-5 border-t border-gray-100 pt-4 text-sm text-gray-700 space-y-3">
          {children}
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      {/* En-tête */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-xl p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <BookOpen size={28} />
          <h2 className="text-2xl font-bold">Guide du CRM Prospection</h2>
        </div>
        <p className="text-emerald-100 text-sm">
          Ce guide décrit le fonctionnement complet du CRM Copro Protect — de l'identification d'un prospect jusqu'à sa conversion — en passant par l'utilisation de SENTINELLE, la gestion des fiches et la méthode SCUD.
        </p>
        {pdfUrl && (
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            download
            className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 bg-white text-emerald-700 rounded-lg font-semibold text-sm hover:bg-emerald-50 transition-colors shadow-sm"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Telecharger la presentation Copro Protect (PDF)
          </a>
        )}
      </div>

      {/* Section 1 — Vision */}
      <Section id="vision" titre="À quoi sert ce CRM ?" emoji="🎯">
        <p>
          Le CRM (Customer Relationship Manager) est l'outil de prospection commerciale de Copro Protect. Son objectif : identifier et activer des partenaires stratégiques — associations, fédérations, professionnels de l'immobilier, médias spécialisés — capables de recommander la plateforme à grande échelle.
        </p>
        <p>
          <strong>Un seul partenariat bien placé peut valoir des centaines d'abonnés.</strong> La logique est claire : mieux vaut convaincre une association nationale de 50 000 adhérents que démarcher 500 copropriétaires un par un.
        </p>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <p className="text-emerald-800 font-semibold text-xs uppercase mb-1">Rôle des apporteurs d'affaires</p>
          <p className="text-emerald-700">
            Les apporteurs d'affaires constituent le premier cercle opérationnel. Leur légitimité terrain, leur connaissance du secteur immobilier et leurs réseaux existants sont des atouts déterminants pour ouvrir des portes que la plateforme seule ne peut pas atteindre. SENTINELLE prépare les messages, l'apporteur les envoie et assure le suivi.
          </p>
        </div>
      </Section>

      {/* Section 2 — Les 5 onglets */}
      <Section id="onglets" titre="Les 5 onglets du CRM" emoji="📑">
        <div className="space-y-4">
          <div className="flex gap-3">
            <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Kanban size={16} className="text-emerald-600" />
            </div>
            <div>
              <p className="font-bold text-gray-800">Pipeline</p>
              <p className="text-gray-600">Le tableau Kanban. Chaque colonne représente une étape de la relation commerciale. Les prospects progressent de colonne en colonne au fil des échanges. C'est la vue centrale de toute la prospection.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Zap size={16} className="text-red-600" />
            </div>
            <div>
              <p className="font-bold text-gray-800">Actions du jour</p>
              <p className="text-gray-600">La vue opérationnelle quotidienne. Répond à la question : <strong>que dois-je faire maintenant ?</strong> Affiche en 4 sections les actions urgentes filtrées par responsable : actions planifiées en retard, brouillons non envoyés depuis plus de 48h, prospects sans contact depuis plus de 14 jours, et réponses reçues sans relance générée. Un badge rouge sur l'onglet signale dès l'ouverture du CRM qu'il y a des actions en attente.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Inbox size={16} className="text-blue-600" />
            </div>
            <div>
              <p className="font-bold text-gray-800">Messagerie</p>
              <p className="text-gray-600">Tous les mails générés par SENTINELLE, classés par statut (brouillon ou envoyé). Un badge orange signale les brouillons en attente d'envoi. Les mails envoyés sont archivés ici.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <List size={16} className="text-purple-600" />
            </div>
            <div>
              <p className="font-bold text-gray-800">Prospects</p>
              <p className="text-gray-600">La liste complète de tous les prospects avec leurs informations principales. Un clic sur une ligne ouvre la fiche détaillée — avec l'historique des échanges, les documents déposés et SENTINELLE CRM.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <LayoutDashboard size={16} className="text-amber-600" />
            </div>
            <div>
              <p className="font-bold text-gray-800">Tableau de bord</p>
              <p className="text-gray-600">Les indicateurs clés (total prospects, convertis, mails envoyés), les prochaines actions planifiées et les alertes actives (brouillons en attente, prospects sans email renseigné).</p>
            </div>
          </div>
        </div>
      </Section>

      {/* Section 3 — La fiche prospect */}
      <Section id="fiche" titre="La fiche prospect" emoji="📋">
        <p>
          Chaque prospect dispose d'une fiche dédiée, accessible depuis le Pipeline ou la liste Prospects. Cette fiche est organisée en trois zones :
        </p>
        <div className="space-y-3 mt-1">
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <p className="font-semibold text-gray-800 text-xs uppercase mb-1">Colonne gauche — Identité &amp; contexte</p>
            <p className="text-gray-600 text-xs">Statut pipeline, segment (Ambassadeur / Client direct / Recommandeur), coordonnées du contact, objectif de la démarche, problématiques connues de la structure, taille estimée, dates clés et responsable du suivi. Ces informations alimentent SENTINELLE pour personnaliser ses réponses.</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <p className="font-semibold text-gray-800 text-xs uppercase mb-1">Colonne centrale — Historique &amp; documents</p>
            <p className="text-gray-600 text-xs">Ajout de notes rapides, dépôt de documents (plaquettes, comptes rendus, propositions commerciales), et timeline complète des échanges (mails générés + notes). Chaque document est typé (Plaquette, Proposition, Compte rendu, Contrat…) et consultable ou téléchargeable.</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
            <p className="font-semibold text-blue-800 text-xs uppercase mb-1">Colonne droite — SENTINELLE CRM</p>
            <p className="text-blue-700 text-xs">L'intelligence artificielle dédiée à ce prospect. SENTINELLE connaît l'intégralité du contexte (structure, historique, statut pipeline) et peut : rédiger un premier mail, analyser un document déposé, préparer une relance contextualisée, anticiper des objections. La conversation est persistée — elle reprend là où elle s'était arrêtée.</p>
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-xs font-semibold text-amber-800 mb-1">Conseil</p>
          <p className="text-xs text-amber-700">Plus la fiche est renseignée au moment d'interroger SENTINELLE, plus les messages générés sont précis et personnalisés. Deux minutes d'enrichissement peuvent faire toute la différence dans le taux de réponse.</p>
        </div>
      </Section>

      {/* Section — Avant le premier mail */}
      <Section id="preparation" titre="Avant le premier mail — qualifier et préparer" emoji="📞">
        <p className="text-gray-600 text-sm mb-3">
          Un mail envoyé sans préparation préalable est du démarchage froid. La méthode SCUD repose
          sur une connaissance de l'interlocuteur et, si possible, un contact préalable — même bref.
          Cette section détaille les 4 étapes à suivre avant d'envoyer le premier mail.
        </p>
        <div className="space-y-4">
          {[
            {
              num: 'A',
              titre: "Créer la fiche avec les infos minimales",
              desc: "Ne pas attendre d'avoir toutes les informations pour créer la fiche. Les données manquantes seront complétées progressivement. L'important est de ne pas perdre une cible identifiée.",
              color: 'bg-gray-100 text-gray-700',
              champs: [
                "Nom de la structure (ex : ARC — Association des Responsables de Copropriété)",
                "Type de structure : association nationale / fédération / prescripteur / individu",
                "Source : comment cette cible a été identifiée (LinkedIn, recommandation, article...)",
              ],
              tip: "Si le nom du contact n'est pas encore connu, laisser le champ vide. SENTINELLE pourra quand même générer un premier mail générique, mais la qualité sera moindre qu'avec un interlocuteur nommé.",
            },
            {
              num: 'B',
              titre: "Qualifier en ligne avant tout contact",
              desc: "Avant d'appeler ou d'écrire, passer 10 minutes sur le site web de la structure et son LinkedIn. L'objectif est d'identifier le bon interlocuteur, de comprendre la taille et les problématiques, et de vérifier si une connexion existante peut être exploitée.",
              color: 'bg-blue-100 text-blue-700',
              champs: [
                "Site web : qui dirige, quel est le périmètre, quelles publications récentes",
                "LinkedIn : trouver le responsable partenariats / adhérents / communication",
                "Problématiques visibles : ce dont la structure parle publiquement (syndics, charges, AG...)",
                "Connexions : quelqu'un dans le réseau connaît-il un membre de cette structure ?",
              ],
              tip: "Renseigner immédiatement ces éléments dans les champs SENTINELLE de la fiche (Objectif de contact, Taille / Adhérents, Source). Ces informations serviront de base à SENTINELLE pour personnaliser le premier mail.",
            },
            {
              num: 'C',
              titre: "L'appel de débroussaillage",
              desc: "Appeler le standard ou le contact identifié. L'objectif est limité et précis : obtenir le nom et l'email du bon interlocuteur, et si possible une permission implicite d'envoyer un mail. Ne pas vendre lors de cet appel — juste qualifier.",
              color: 'bg-emerald-100 text-emerald-700',
              champs: [
                "Bonjour, je cherche la personne en charge des partenariats ou des adhérents — vous pouvez m'indiquer à qui m'adresser ?",
                "Obtenir : prénom, nom, email direct",
                "Si l'interlocuteur est curieux : 2 phrases de contexte max, puis lui proposer d'envoyer une présentation par mail qu'il pourra lire à tête reposée",
              ],
              tip: "Après l'appel, ajouter une note dans la fiche — exemple : Appel 12/03, contact : Marie Dupont, directrice, marie@arc.fr, impression : ouverte, attend le mail. SENTINELLE utilisera cette note pour personnaliser le premier message.",
            },
            {
              num: 'D',
              titre: "Si appel impossible — workflow alternatif",
              desc: "Certaines structures sont difficiles à joindre par téléphone (associations bénévoles, fédérations nationales…). Si après 2 tentatives d'appel à des horaires différents il n'y a pas de réponse, on peut procéder au mail froid — mais en compensant par une personnalisation maximale via les informations collectées en ligne.",
              color: 'bg-orange-100 text-orange-700',
              champs: [
                "Tenter 2 appels à des horaires différents (matin / après-midi) avant de basculer en mail froid",
                "Indiquer à SENTINELLE : pas de contact téléphonique obtenu malgré 2 tentatives",
                "Demander à SENTINELLE de compenser par une personnalisation maximale basée sur la qualification en ligne",
              ],
              tip: null,
            },
          ].map((step) => (
            <div key={step.num} className="flex gap-3">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm ${step.color}`}>
                {step.num}
              </div>
              <div className="flex-1">
                <p className="font-bold text-gray-800">{step.titre}</p>
                <p className="text-gray-600 text-xs mt-0.5 mb-2">{step.desc}</p>
                {step.champs && (
                  <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 mb-2">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">À collecter / À faire</p>
                    <ul className="space-y-1">
                      {step.champs.map((c, i) => (
                        <li key={i} className="flex gap-1.5 text-xs text-gray-600">
                          <ChevronRight size={11} className="mt-0.5 flex-shrink-0 text-gray-400" />
                          <span>{c}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {step.tip && (
                  <div className="bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5">
                    <p className="text-xs text-amber-700"><strong>Note CRM :</strong> {step.tip}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mt-2">
          <p className="text-xs font-semibold text-gray-600 mb-2">Champs à renseigner avant d'interroger SENTINELLE</p>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-red-50 border border-red-200 rounded p-2">
              <p className="text-[10px] font-bold text-red-600 uppercase tracking-wider mb-1">Obligatoires</p>
              <ul className="space-y-0.5 text-xs text-gray-700">
                <li>• Nom du contact (prénom + nom)</li>
                <li>• Email direct</li>
                <li>• Objectif du contact</li>
                <li>• Taille / Adhérents</li>
              </ul>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded p-2">
              <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider mb-1">Fortement recommandés</p>
              <ul className="space-y-0.5 text-xs text-gray-700">
                <li>• Problématiques connues</li>
                <li>• Source de la cible</li>
                <li>• Note après appel</li>
                <li>• Site web / LinkedIn</li>
              </ul>
            </div>
          </div>
        </div>
      </Section>



      {/* Section 4 — Workflow */}
      <Section id="workflow" titre="Workflow pas à pas" emoji="🔄">
        <p className="font-semibold text-gray-800 mb-2">De l'identification d'un prospect à la conversion — 6 étapes :</p>
        <div className="space-y-3">
          {[
            {
              num: '1',
              titre: 'Créer le prospect',
              desc: 'Dans l\'onglet Pipeline, cliquer "Nouveau prospect". Renseigner le nom de la structure, le type, le nom du contact et son email. Le prospect est créé en statut "Identifié".',
              color: 'bg-gray-100 text-gray-700',
            },
            {
              num: '2',
              titre: 'Enrichir la fiche',
              desc: 'Cliquer sur la carte dans le Pipeline pour ouvrir la fiche. Compléter les données SENTINELLE : problématiques connues, objectif de la démarche, taille de la structure. Plus le contexte est détaillé, plus SENTINELLE sera pertinent.',
              color: 'bg-amber-100 text-amber-700',
            },
            {
              num: '3',
              titre: 'Interroger SENTINELLE',
              desc: 'Dans la colonne droite de la fiche, utiliser SENTINELLE CRM pour générer un premier mail, analyser un document déposé ou préparer une stratégie d\'approche. Les suggestions en bas du chat proposent des points de départ selon le statut du prospect.',
              color: 'bg-blue-100 text-blue-700',
            },
            {
              num: '4',
              titre: 'Envoyer le mail',
              desc: 'SENTINELLE rédige le contenu dans le chat. Le copier, ouvrir Gmail, et envoyer depuis votre boîte habituelle. Le mail envoyé est automatiquement enregistré dans l\'onglet Messagerie de la fiche.',
              color: 'bg-emerald-100 text-emerald-700',
            },
            {
              num: '5',
              titre: 'Traiter la réponse du prospect',
              desc: 'Quand le prospect répond, lire sa réponse dans Gmail, l\'imprimer ou l\'exporter en PDF, puis l\'importer dans l\'onglet Messagerie via le bouton "Importer une réponse reçue". SENTINELLE analyse le PDF : elle identifie les points traités, les points ignorés, qualifie la réponse (favorable / neutre / évasif) et propose une relance SCUD prête à envoyer si nécessaire.',
              color: 'bg-orange-100 text-orange-700',
            },
            {
              num: '6',
              titre: 'Faire avancer le pipeline',
              desc: 'Après chaque échange significatif, faire glisser la carte vers la colonne suivante. Cela tient à jour l\'état réel de la relation et oriente les suggestions de SENTINELLE pour la suite.',
              color: 'bg-purple-100 text-purple-700',
            },
            {
              num: '7',
              titre: 'Relancer et conclure',
              desc: 'Pas de réponse après 10 jours ? Demander à SENTINELLE une relance contextualisée depuis la fiche. Ne jamais relancer plus de deux fois. Après une conversion, marquer le prospect comme "Converti" et documenter l\'accord.',
              color: 'bg-rose-100 text-rose-700',
            },
          ].map((step) => (
            <div key={step.num} className="flex gap-3">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm ${step.color}`}>
                {step.num}
              </div>
              <div>
                <p className="font-bold text-gray-800">{step.titre}</p>
                <p className="text-gray-600 mt-0.5">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Section 5 — SENTINELLE CRM */}
      <Section id="sentinelle" titre="SENTINELLE et la méthode SCUD" emoji="✨">
        <p>
          SENTINELLE est l'intelligence artificielle au cœur de Copro Protect. Dans le CRM, elle est spécialisée sur la relation commerciale : elle connaît le profil du prospect, l'historique des échanges et le statut pipeline pour produire des messages sur mesure.
        </p>
        <div className="bg-gray-50 rounded-lg p-3 mb-1">
          <p className="font-semibold text-gray-700 text-xs uppercase mb-1">Ce que SENTINELLE peut faire dans une fiche prospect</p>
          <ul className="text-xs text-gray-600 space-y-1">
            <li className="flex gap-2"><CheckCircle2 size={13} className="text-emerald-500 mt-0.5 flex-shrink-0" /><span>Rédiger un premier mail de contact personnalisé selon la structure et ses problématiques</span></li>
            <li className="flex gap-2"><CheckCircle2 size={13} className="text-emerald-500 mt-0.5 flex-shrink-0" /><span>Analyser un document déposé (plaquette, statuts, compte rendu) pour en extraire les points saillants</span></li>
            <li className="flex gap-2"><CheckCircle2 size={13} className="text-emerald-500 mt-0.5 flex-shrink-0" /><span>Préparer une relance contextualisée en tenant compte des échanges précédents</span></li>
            <li className="flex gap-2"><CheckCircle2 size={13} className="text-emerald-500 mt-0.5 flex-shrink-0" /><span>Anticiper les objections selon le type de structure (association, syndic, média…)</span></li>
            <li className="flex gap-2"><CheckCircle2 size={13} className="text-emerald-500 mt-0.5 flex-shrink-0" /><span>Proposer des angles d'approche différents si une tentative n'a pas abouti</span></li>
          </ul>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
          <p className="font-bold text-blue-800 text-xs uppercase">Les 5 piliers SCUD — méthode de communication diplomatique</p>
          {[
            { pilier: '1 — Posture du béotien naïf', desc: 'Ne pas se présenter en vendeur cherchant à convaincre. Se positionner comme quelqu\'un qui découvre, qui cherche conseil. Cette posture désarme l\'interlocuteur et l\'incite à expliquer plutôt que rejeter.' },
            { pilier: '2 — Flatter le professionnalisme', desc: 'Reconnaître explicitement l\'expertise et le rôle de la structure. Cela crée une obligation morale de répondre à la hauteur de sa réputation.' },
            { pilier: '3 — Questions, pas affirmations', desc: 'Ne jamais affirmer ce que l\'autre devrait faire. Poser des questions : "est-ce que ce problème résonne avec ce que vivent vos adhérents ?" Une question ne peut pas être attaquée.' },
            { pilier: '4 — Documenter sans interpréter', desc: 'S\'appuyer sur des faits connus (taille de la structure, problématiques du secteur) sans extrapoler ni sur-promettre. Les faits créent la crédibilité.' },
            { pilier: '5 — Remercier d\'abord', desc: 'Commencer par reconnaître ce que fait déjà l\'interlocuteur pour ses adhérents ou son lectorat, avant d\'aborder le sujet. Ce réflexe établit un climat de respect mutuel.' },
          ].map((item) => (
            <div key={item.pilier} className="border-l-2 border-blue-300 pl-3">
              <p className="font-semibold text-blue-700 text-xs">{item.pilier}</p>
              <p className="text-gray-600 text-xs mt-0.5">{item.desc}</p>
            </div>
          ))}
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="font-semibold text-gray-700 text-xs uppercase mb-1">Pourquoi SENTINELLE surpasse les templates</p>
          <p className="text-gray-600 text-xs">Un template envoie le même texte à tout le monde. SENTINELLE connaît la taille de la structure, ses problématiques spécifiques, le statut dans le pipeline et l'historique des échanges — et rédige un message qui semble écrit à la main, pour cette structure en particulier. Le taux de réponse n'est pas comparable.</p>
        </div>
      </Section>

      {/* Section 6 — Bonnes pratiques */}
      <Section id="pratiques" titre="Bonnes pratiques" emoji="💡">
        <div className="space-y-3">
          {[
            { titre: 'Renseigner l\'email en priorité', desc: 'Sans adresse email, SENTINELLE ne peut pas générer de mail opérationnel. C\'est la première information à obtenir pour tout nouveau prospect.' },
            { titre: 'Enrichir avant d\'interroger SENTINELLE', desc: 'Plus les données de la fiche sont complètes (problématiques, objectif, taille, historique), plus SENTINELLE produit un message pertinent. Ce travail préalable est un investissement rentable.' },
            { titre: 'Documenter chaque appel dans les notes', desc: 'Après chaque appel téléphonique, ajouter une note dans la fiche (date, interlocuteur, impression, prochaine étape). SENTINELLE lit ces notes pour personnaliser ses messages — plus le contexte est riche, plus les relances sont précises.' },
            { titre: 'Faire avancer le pipeline régulièrement', desc: 'Un prospect qui stagne dans "Identifié" depuis deux semaines est une opportunité qui se perd. Le Tableau de bord met en évidence les prospects sans activité récente.' },
            { titre: 'Prioriser les Ambassadeurs', desc: 'ARC, UNARC, UNPI, CLCV, AFCopro… Un accord avec l\'une de ces structures peut valoir des centaines d\'abonnés. Ces cibles doivent passer avant tout démarchage individuel.' },
            { titre: 'Respecter le rythme SCUD', desc: 'Un premier contact, une relance à J+10, une dernière à J+30 si nécessaire. La pression commerciale est incompatible avec la méthode SCUD et détériore la relation.' },
            { titre: 'Déposer les documents clés dans la fiche', desc: 'Plaquette envoyée, proposition commerciale, compte rendu d\'échange — chaque document déposé enrichit le contexte disponible pour SENTINELLE lors des interactions suivantes.' },
          ].map((item) => (
            <div key={item.titre} className="flex gap-2">
              <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-800">{item.titre}</p>
                <p className="text-gray-500 text-xs mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Section 7 — Les 3 segments */}
      <Section id="prospects-segment" titre="Les 3 segments de prospects" emoji="🏆">
        <p>Chaque prospect est classé dans l'un des 3 segments. Ce classement détermine la priorité et la stratégie d'approche, et oriente automatiquement les suggestions de SENTINELLE.</p>
        <div className="space-y-4 mt-3">
          {[
            {
              label: 'Ambassadeur',
              soustitre: 'Un accord unique, un impact massif — Priorité absolue',
              desc: 'Recommande Copro Protect à toute sa communauté via sa newsletter, son site, ses événements. Un seul partenariat peut générer des centaines d\'abonnés sans effort de prospection supplémentaire.',
              exemples: 'ARC, UNARC, UNPI, ANCC, CLCV, AFCopro. Également : les syndics professionnels modernes qui intègrent Copro Protect dans leur offre et le recommandent à leurs copropriétés clientes.',
              color: 'bg-emerald-50 border-emerald-300',
              titleColor: 'text-emerald-800',
              descColor: 'text-emerald-700',
            },
            {
              label: 'Client direct',
              soustitre: 'Prospect et futur utilisateur — Priorité immédiate pour le chiffre d\'affaires',
              desc: 'Démarché directement, il souscrit pour lui-même. C\'est le carburant court terme — chaque conversion génère du revenu récurrent dès le lendemain.',
              exemples: 'Président de CS d\'une résidence de 30 lots, copropriétaire bailleur isolé, syndic bénévole souhaitant outiller son conseil syndical.',
              color: 'bg-purple-50 border-purple-300',
              titleColor: 'text-purple-800',
              descColor: 'text-purple-700',
            },
            {
              label: 'Recommandeur',
              soustitre: 'Il conseille, ses clients achètent — Priorité long terme',
              desc: 'Oriente ponctuellement vers Copro Protect sans l\'utiliser lui-même. Précieux mais plus lent à activer — une relation de confiance dans le produit doit d\'abord être établie.',
              exemples: 'Avocat spécialisé copropriété, notaire, expert-comptable de syndic, journaliste immobilier. Également : syndic professionnel qui perçoit Copro Protect comme un outil de qualité sans vouloir en assurer la distribution.',
              color: 'bg-blue-50 border-blue-300',
              titleColor: 'text-blue-800',
              descColor: 'text-blue-700',
            },
          ].map((item) => (
            <div key={item.label} className={`rounded-xl p-4 border-2 ${item.color}`}>
              <p className={`font-bold text-sm ${item.titleColor}`}>{item.label}</p>
              <p className={`text-xs font-semibold mb-2 mt-0.5 ${item.titleColor} opacity-75`}>{item.soustitre}</p>
              <p className={`text-xs mb-2 ${item.descColor}`}>{item.desc}</p>
              <div className="bg-white/60 rounded-lg px-3 py-2">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-0.5">Exemples</p>
                <p className={`text-xs ${item.descColor}`}>{item.exemples}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
          <p className="text-xs font-semibold text-amber-800 mb-1">Note sur les syndics professionnels</p>
          <p className="text-xs text-amber-700">
            Un syndic professionnel peut être Ambassadeur <strong>ou</strong> Recommandeur selon sa posture. Le syndic moderne et transparent verra dans un CS outillé un gage de qualité — il peut devenir un vecteur de diffusion puissant. Le syndic frileux craindra la transparence accrue — inutile d'y investir du temps.
          </p>
        </div>
      </Section>

      {/* Section Prompt SENTINELLE V3 */}
      <Section id="prompt-sentinelle" titre="SENTINELLE Prospection — Comment ça marche (V3)" emoji="🤖">
        <div className="space-y-4 text-sm text-gray-700">

          {/* Intro */}
          <p>
            Le prompt SENTINELLE Prospection V3 est une <strong>session Claude autonome</strong> — il fonctionne en dehors de l'application, dans Claude.ai, en collant le fichier comme prompt système d'un Project dédié. SENTINELLE prend le lead : elle pose les questions, guide la collecte et produit les livrables.
          </p>

          {/* Les 3 phases */}
          <div className="space-y-3">
            <p className="font-semibold text-gray-800">Le workflow en 3 phases :</p>
            {[
              {
                num: '1',
                titre: 'Ciblage',
                desc: 'SENTINELLE ouvre la session et pose 3 à 4 questions pour identifier le type de prospect (ambassadeur, client direct, recommandeur), la zone géographique et le responsable (Dominique ou Alain). Si une piste existe déjà, elle saute directement à la phase suivante.',
                color: 'bg-blue-100 text-blue-700',
              },
              {
                num: '2',
                titre: 'Enrichissement de la fiche',
                desc: 'SENTINELLE demande si on est avant ou après l\'appel, puis adapte ses questions. Avant l\'appel : coordonnées, segment, produit visé, objectif, contexte connu. Après l\'appel : problématiques identifiées, niveau d\'intérêt, statut pipeline mis à jour, prochaine action.',
                color: 'bg-amber-100 text-amber-700',
              },
              {
                num: '3',
                titre: 'Génération des 2 livrables HTML',
                desc: 'En fin de session, SENTINELLE produit automatiquement deux fichiers HTML : (1) une fiche CRM structurée avec tous les champs prêts à saisir dans Copro Protect, (2) une fiche visuelle premium avec analyse stratégique, leviers colorés et recommandation de ton.',
                color: 'bg-emerald-100 text-emerald-700',
              },
            ].map((step) => (
              <div key={step.num} className="flex gap-3">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm ${step.color}`}>
                  {step.num}
                </div>
                <div>
                  <p className="font-bold text-gray-800">{step.titre}</p>
                  <p className="text-gray-600 text-xs mt-0.5">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Les 2 livrables */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Les 2 livrables produits par SENTINELLE</p>
            <div className="flex gap-2">
              <div className="flex-1 bg-white border border-gray-200 rounded-lg p-2">
                <p className="text-xs font-bold text-gray-700 mb-1">Livrable 1 — Fiche CRM</p>
                <p className="text-xs text-gray-500">Tous les champs organisés par section (Identité, Contact, Données, Prospection, Notes). Bouton "Copier tout". Prêt à saisir dans le CRM.</p>
              </div>
              <div className="flex-1 bg-white border border-gray-200 rounded-lg p-2">
                <p className="text-xs font-bold text-gray-700 mb-1">Livrable 2 — Fiche visuelle</p>
                <p className="text-xs text-gray-500">Design premium avec analyse stratégique SENTINELLE : levier principal, frein anticipé, ton recommandé, leviers vert/orange/rouge. Imprimable.</p>
              </div>
            </div>
          </div>

          {/* Règle clé */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-blue-800 mb-1">Règle d'or — Une question à la fois</p>
            <p className="text-xs text-blue-700">
              SENTINELLE ne pose jamais deux questions simultanément. Elle attend la réponse, accuse réception en une phrase, puis continue. Si une information est inconnue, elle note "Non renseigné" et passe à la suite. Pas de blocage.
            </p>
          </div>

          {/* Comment utiliser */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-amber-800 mb-1">Comment utiliser ce prompt</p>
            <ol className="text-xs text-amber-700 space-y-1 list-decimal list-inside">
              <li>Télécharger le fichier .md ci-dessous</li>
              <li>Ouvrir <strong>claude.ai</strong> → aller dans un Project dédié "Prospection Copro Protect"</li>
              <li>Coller le contenu du fichier comme <strong>prompt système du Project</strong></li>
              <li>Démarrer une nouvelle conversation — SENTINELLE ouvre automatiquement la session</li>
              <li>À la fin, copier les livrables HTML et saisir les données dans le CRM</li>
            </ol>
          </div>

          {/* Bouton téléchargement */}
          <div className="pt-1">
            <button
              onClick={() => {
                const contenu = PROMPT_ENRICHISSEMENT_PROSPECTS;
                const blob = new Blob([contenu], { type: 'text/markdown;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'SENTINELLE_PROSPECTION_V3_COPRO_PROTECT.md';
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
            >
              ⬇ Télécharger le prompt V3 (fichier .md)
            </button>
            <p className="text-xs text-gray-400 mt-1.5">Si le fichier est perdu, il est toujours récupérable ici.</p>
          </div>
        </div>
      </Section>

      {/* Footer */}
      <div className="bg-gray-50 rounded-xl p-4 text-center border border-gray-200">
        <p className="text-sm text-gray-500">
          Pour toute question sur l'utilisation du CRM, contacter l'équipe Copro Protect.
        </p>
        <p className="text-xs text-gray-400 mt-1">Guide CRM — Session 220 — Prompt SENTINELLE Prospection V3</p>
      </div>
    </div>
  );
};

// ============================================
// COMPOSANT PRINCIPAL — CRM avec onglets
// ============================================

const CrmPipelinePage: React.FC = () => {
  // ---- État global ----
  const navigate = useNavigate();
  const [ongletActif, setOngletActif] = useState<OngletCRM>('pipeline');
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [responsables, setResponsables] = useState<InfoResponsable[]>([]);
  const [loading, setLoading] = useState(true);

  // ---- État Pipeline ----
  const [filtreRecherche, setFiltreRecherche] = useState('');
  const [filtreResponsable, setFiltreResponsable] = useState<string>('tous');
  const [filtreSegment, setFiltreSegment] = useState<SegmentProspect | 'tous'>('tous');
  const [showPerdus, setShowPerdus] = useState(false);
  const draggedProspect = useRef<Prospect | null>(null);

  // ---- État modales ----
  const [modalCreation, setModalCreation] = useState(false);
  const [prospectEdition, setProspectEdition] = useState<Prospect | null>(null);
  const [prospectMail, setProspectMail] = useState<Prospect | null>(null);
  const [showApporteurModal, setShowApporteurModal] = useState(false);
  const [prospectAMarquerContacte, setProspectAMarquerContacte] = useState<Prospect | null>(null);

  // ---- État Gmail ----
  const [gmailConnecte, setGmailConnecte] = useState(false);
  const [gmailEmail, setGmailEmail] = useState('');

  // ---- État seed/purge ----
  const [seedEnCours, setSeedEnCours] = useState(false);
  const [confirmPurge, setConfirmPurge] = useState(false);
  const [purgeEnCours, setPurgeEnCours] = useState(false);

  // ---- Chargement ----
  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([chargerProspects(), chargerResponsables()]);
      setProspects(p);
      setResponsables(r);
    } catch (err) {
      console.error('[CRM] Erreur chargement:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    charger();

    const checkGmail = async () => {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const firebaseModule = await import('../../services/firebase');
        const uid = firebaseModule.auth.currentUser?.uid;
        if (!uid) return;
        const userSnap = await getDoc(doc(firebaseModule.db, 'users', uid));
        if (userSnap.exists()) {
          const data = userSnap.data();
          setGmailConnecte(!!data.gmailCrmConnecte);
          setGmailEmail(data.gmailCrmEmail || '');
        }
      } catch (err) {
        console.warn('[CRM] Erreur vérification Gmail:', err);
      }
    };
    checkGmail();

    const params = new URLSearchParams(window.location.search);
    if (params.get('gmail') === 'connected') {
      setGmailConnecte(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [charger]);

  // ---- Filtrage Pipeline ----
  const prospectsFiltres = prospects.filter((p) => {
    if (filtreResponsable !== 'tous' && p.responsable !== filtreResponsable) return false;
    if (filtreSegment !== 'tous' && p.segment !== filtreSegment) return false;
    if (filtreRecherche) {
      const search = filtreRecherche.toLowerCase();
      return (
        p.nom.toLowerCase().includes(search) ||
        (p.contactNom || '').toLowerCase().includes(search) ||
        (p.contactEmail || '').toLowerCase().includes(search) ||
        (p.objectifContact || '').toLowerCase().includes(search) ||
        (p.problematiques || []).some((prob) => prob.toLowerCase().includes(search))
      );
    }
    return true;
  });

  // Ordre de priorité des segments dans chaque colonne
  const ORDRE_SEGMENT: Record<string, number> = {
    ambassadeur: 0,
    client_direct: 1,
    recommandeur: 2,
  };

  const prospectParStatut = (statut: StatutProspect): Prospect[] =>
    prospectsFiltres
      .filter((p) => p.statut === statut)
      .sort((a, b) => (ORDRE_SEGMENT[a.segment] ?? 9) - (ORDRE_SEGMENT[b.segment] ?? 9));

  // ---- Colonnes affichées ----
  const handleDragStart = (_e: React.DragEvent, prospect: Prospect) => {
    draggedProspect.current = prospect;
  };

  const handleDrop = async (nouveauStatut: StatutProspect) => {
    const prospect = draggedProspect.current;
    if (!prospect || prospect.statut === nouveauStatut) return;
    setProspects((prev) => prev.map((p) => (p.id === prospect.id ? { ...p, statut: nouveauStatut } : p)));
    try {
      await changerStatutProspect(prospect.id, nouveauStatut);
      if (nouveauStatut === 'converti' && prospect.contactEmail) {
        const result = await rapprocherProspectUser(prospect.id);
        if (result.found) console.log(`[CRM] Rapprochement auto: ${result.userId}`);
      }
      await charger();
    } catch (err) {
      console.error('[CRM] Erreur changement statut:', err);
      await charger();
    }
  };

  // ---- Actions ----
  const handleCreer = async (data: Parameters<typeof creerProspect>[0]) => {
    await creerProspect(data);
    await charger();
  };

  const handleModifier = async (data: Parameters<typeof creerProspect>[0]) => {
    if (!prospectEdition) return;
    await mettreAJourProspect(prospectEdition.id, {
      nom: data.nom, typeProspect: data.typeProspect, typeCible: data.typeCible, responsable: data.responsable,
      contactNom: data.contactNom || '', contactEmail: data.contactEmail || '',
      contactTelephone: data.contactTelephone || '', adresse: data.adresse || '',
      siteWeb: data.siteWeb || '', source: data.source || '',
      problematiques: data.problematiques || [], objectifContact: data.objectifContact || '',
      tailleStructure: data.tailleStructure || '', prochaineAction: data.prochaineAction || '',
      dateProchaineAction: data.dateProchaineAction || undefined,
    });
    setProspectEdition(null);
    await charger();
  };

  const handleChangerResponsable = async (prospectId: string, responsable: ResponsableProspect) => {
    setProspects((prev) => prev.map((p) => (p.id === prospectId ? { ...p, responsable } : p)));
    await changerResponsableProspect(prospectId, responsable);
  };

  const handleAjouterNote = async (prospectId: string, texte: string) => {
    const auteur = responsables.length > 0 ? responsables[0].identifiant : 'admin';
    await ajouterNote(prospectId, texte, auteur);
    await charger();
  };

  const handleSupprimer = async (prospectId: string) => {
    await supprimerProspect(prospectId);
    await charger();
  };

  const handleRapprocher = async (prospectId: string) => {
    const result = await rapprocherProspectUser(prospectId);
    if (result.found) {
      alert(`Client trouvé ! Formule : ${result.formule}`);
      await charger();
    } else {
      alert('Aucun compte Copro Protect trouvé avec cet email.');
    }
  };

  const handleSeed = async () => {
    setSeedEnCours(true);
    try {
      const count = await seedProspectsInitiaux();
      await charger();
      console.log(`[CRM Seed] ${count} prospects chargés.`);
    } catch (err) {
      console.error('[CRM Seed] Erreur:', err);
      alert('Erreur lors du chargement des prospects initiaux.');
    } finally {
      setSeedEnCours(false);
    }
  };

  const handlePurge = async () => {
    setPurgeEnCours(true);
    try {
      const count = await purgerTousLesProspects();
      alert(`${count} prospects supprimés.`);
      setConfirmPurge(false);
      await charger();
    } catch (err) {
      console.error('[CRM] Erreur purge:', err);
      alert('Erreur lors de la purge.');
    } finally {
      setPurgeEnCours(false);
    }
  };

  const handleNaviguerVersFiche = (prospect: Prospect) => navigate(`/admin-saas/crm/prospect/${prospect.id}`);
  const handleOuvrirEdition = (prospect: Prospect) => setProspectEdition(prospect);
  const colonnesAffichees = showPerdus ? [...COLONNES_KANBAN, 'perdu' as StatutProspect] : COLONNES_KANBAN;

  const handleMailGenere = async () => {
    const fresh = await chargerProspects();
    setProspects(fresh);
  };

  // ---- Définition des onglets ----
  const onglets: Array<{ id: OngletCRM; label: string; icon: React.ReactNode; badge?: number }> = [
    { id: 'pipeline', label: 'Pipeline', icon: <Kanban size={16} /> },
    {
      id: 'actions-du-jour',
      label: 'Actions du jour',
      icon: <Zap size={16} />,
      badge: (() => {
        const actifs = prospects.filter((p) => {
          if (p.statut === 'converti' || p.statut === 'perdu') return false;
          if (filtreResponsable !== 'tous' && p.responsable !== filtreResponsable) return false;
          return true;
        });
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const n1 = actifs.filter((p) => {
          if (!p.dateProchaineAction) return false;
          const d = p.dateProchaineAction.toDate(); d.setHours(0, 0, 0, 0);
          return d <= today;
        }).length;
        const seuilB = new Date(today.getTime() - 48 * 60 * 60 * 1000);
        const n2 = actifs.reduce((acc, p) => acc + (p.historiqueMails || []).filter((m) => !m.envoye && (!m.type || m.type === 'brouillon') && (m.date?.toDate?.() || new Date()) < seuilB).length, 0);
        const seuilI = new Date(today.getTime() - 14 * 24 * 60 * 60 * 1000);
        const n3 = actifs.filter((p) => getDerniereActivite(p) < seuilI).length;
        const n4 = actifs.filter((p) => hasReponseEnAttente(p)).length;
        return (n1 + n2 + n3 + n4) || undefined;
      })(),
    },
    {
      id: 'messagerie', label: 'Messagerie', icon: <Inbox size={16} />,
      badge: prospects.flatMap((p) => p.historiqueMails || []).filter((m) => !m.envoye).length || undefined,
    },
    { id: 'prospects', label: 'Prospects', icon: <List size={16} /> },
    { id: 'tableau-bord', label: 'Tableau de bord', icon: <LayoutDashboard size={16} /> },
    { id: 'guide', label: 'Guide', icon: <BookOpen size={16} /> },
  ];

  return (
    <div className="space-y-4">
      {/* Header global */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">CRM Prospection</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {prospects.length} prospect{prospects.length > 1 ? 's' : ''} — {prospects.filter((p) => p.statut === 'converti').length} converti{prospects.filter((p) => p.statut === 'converti').length > 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={charger} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" title="Rafraîchir">
            <RefreshCw size={18} />
          </button>

          {/* Purge */}
          {prospects.length > 0 && (
            confirmPurge ? (
              <div className="flex items-center gap-2 bg-red-50 px-3 py-1.5 rounded-lg border border-red-200">
                <span className="text-xs text-red-600 font-semibold">Tout supprimer ?</span>
                <button onClick={handlePurge} disabled={purgeEnCours} className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:opacity-50">
                  {purgeEnCours ? 'Purge...' : 'Confirmer'}
                </button>
                <button onClick={() => setConfirmPurge(false)} className="px-3 py-1 bg-gray-200 text-gray-600 text-xs rounded hover:bg-gray-300">Non</button>
              </div>
            ) : (
              <button onClick={() => setConfirmPurge(true)} className="p-2 text-red-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Purger tous les prospects">
                <Trash2 size={18} />
              </button>
            )
          )}

          {/* Gmail */}
          {gmailConnecte ? (
            <div className="flex items-center gap-1.5 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700" title={gmailEmail}>
              <Mail size={14} />
              <span className="font-semibold">{gmailEmail ? gmailEmail.split('@')[0] : 'Gmail'}</span>
              <Check size={12} />
            </div>
          ) : (
            <button onClick={async () => { try { const url = await getGmailAuthUrlCrm(); window.location.href = url; } catch (err) { alert('Erreur connexion Gmail.'); } }}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold text-sm transition-colors">
              <Mail size={16} /> Connecter Gmail
            </button>
          )}

          <button onClick={() => setShowApporteurModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold text-sm transition-colors shadow-sm">
            <Handshake size={18} /> Apporteurs
          </button>
          <button onClick={() => setModalCreation(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold text-sm transition-colors shadow-sm">
            <Plus size={18} /> Nouveau prospect
          </button>
        </div>
      </div>

      {/* Barre d'onglets */}
      <div className="flex items-center gap-1 bg-white rounded-xl shadow-sm p-1 border border-gray-200">
        {onglets.map((onglet) => (
          <button key={onglet.id} onClick={() => setOngletActif(onglet.id)}
            className={`relative flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
              ongletActif === onglet.id
                ? 'bg-emerald-600 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}>
            {onglet.icon}
            {onglet.label}
            {onglet.badge !== undefined && onglet.badge > 0 && (
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                ongletActif === onglet.id
                  ? 'bg-white text-emerald-600'
                  : onglet.id === 'actions-du-jour'
                    ? 'bg-red-500 text-white'
                    : 'bg-amber-500 text-white'
              }`}>
                {onglet.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Contenu selon l'onglet actif */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
        </div>
      ) : (
        <>
          {/* ---- PIPELINE ---- */}
          {ongletActif === 'pipeline' && (
            <div className="space-y-4">
              {/* Filtres Pipeline */}
              <div className="flex flex-wrap items-center gap-3 bg-white p-3 rounded-xl shadow-sm">
                <div className="relative flex-1 min-w-[200px]">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="text" value={filtreRecherche} onChange={(e) => setFiltreRecherche(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                    placeholder="Rechercher un prospect..." />
                </div>
                {/* Filtre responsable */}
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                  <button onClick={() => setFiltreResponsable('tous')}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${filtreResponsable === 'tous' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                    Tous
                  </button>
                  {responsables.map((r) => (
                    <button key={r.identifiant} onClick={() => setFiltreResponsable(r.identifiant)}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${filtreResponsable === r.identifiant ? `${r.couleurClasse} shadow-sm` : 'text-gray-500 hover:text-gray-700'}`}>
                      {r.nomComplet.split(' ')[0]}
                    </button>
                  ))}
                </div>
                {/* Filtre segment */}
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                  {([
                    { val: 'tous', label: 'Tous segments' },
                    { val: 'ambassadeur', label: 'Ambassadeurs' },
                    { val: 'client_direct', label: 'Clients directs' },
                    { val: 'recommandeur', label: 'Recommandeurs' },
                  ] as const).map(({ val, label }) => (
                    <button key={val} onClick={() => setFiltreSegment(val)}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                        filtreSegment === val
                          ? val === 'ambassadeur' ? 'bg-white text-emerald-700 shadow-sm'
                          : val === 'client_direct' ? 'bg-white text-purple-700 shadow-sm'
                          : val === 'recommandeur' ? 'bg-white text-blue-700 shadow-sm'
                          : 'bg-white text-gray-800 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}>
                      {label}
                    </button>
                  ))}
                </div>
                <button onClick={() => setShowPerdus(!showPerdus)}
                  className={`flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border transition-colors ${showPerdus ? 'bg-red-50 text-red-600 border-red-200' : 'text-gray-400 border-gray-200 hover:text-gray-600'}`}>
                  {showPerdus ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  Perdus ({prospects.filter((p) => p.statut === 'perdu').length})
                </button>
              </div>

              {/* Kanban */}
              {prospects.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl shadow-sm gap-3">
                  <Target size={48} className="text-gray-300" />
                  <h3 className="text-lg font-bold text-gray-700">Pipeline vide</h3>
                  <p className="text-sm text-gray-500 text-center max-w-md">
                    Crée ton premier prospect ou charge les 18 prospects initiaux.
                  </p>
                  <div className="flex gap-3 mt-2">
                    <button onClick={() => setModalCreation(true)}
                      className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold transition-colors shadow-sm">
                      <Plus size={18} /> Nouveau prospect
                    </button>
                    <button onClick={handleSeed} disabled={seedEnCours}
                      className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors shadow-sm disabled:opacity-50">
                      <Sparkles size={18} /> {seedEnCours ? 'Chargement...' : 'Charger les 18 prospects'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-3 overflow-x-auto pb-4">
                  {colonnesAffichees.map((statut) => (
                    <ColonneKanban
                      key={statut} statut={statut} prospects={prospectParStatut(statut)}
                      responsables={responsables} onDragStart={handleDragStart} onDrop={handleDrop}
                      onOuvrirDetail={handleNaviguerVersFiche} onChangerResponsable={handleChangerResponsable}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ---- ACTIONS DU JOUR ---- */}
          {ongletActif === 'actions-du-jour' && (
            <OngletActionsDuJour
              prospects={prospects}
              responsables={responsables}
              filtreResponsable={filtreResponsable}
              onOuvrirProspect={handleNaviguerVersFiche}
            />
          )}

          {/* ---- MESSAGERIE ---- */}
          {ongletActif === 'messagerie' && (
            <OngletMessagerie
              prospects={prospects}
              responsables={responsables}
              onOuvrirProspect={handleNaviguerVersFiche}
              onGenererMail={(p) => setProspectMail(p)}
              onGmailOuvert={(p) => setProspectAMarquerContacte(p)}
              onSupprime={charger}
            />
          )}

          {/* ---- PROSPECTS ---- */}
          {ongletActif === 'prospects' && (
            <OngletProspects
              prospects={prospects}
              responsables={responsables}
              onOuvrirDetail={handleNaviguerVersFiche}
              onCreer={() => setModalCreation(true)}
            />
          )}

          {/* ---- TABLEAU DE BORD ---- */}
          {ongletActif === 'tableau-bord' && (
            <OngletTableauBord prospects={prospects} responsables={responsables} />
          )}

          {/* ---- GUIDE ---- */}
          {ongletActif === 'guide' && (
            <OngletGuide />
          )}
        </>
      )}

      {/* Modales globales */}
      {modalCreation && (
        <ModalProspect responsables={responsables} onFermer={() => setModalCreation(false)} onSauvegarder={handleCreer} />
      )}
      {prospectEdition && (
        <ModalProspect prospect={prospectEdition} responsables={responsables}
          onFermer={() => setProspectEdition(null)} onSauvegarder={handleModifier} onSupprimer={handleSupprimer} />
      )}
      {prospectMail && (
        <ModalMailSentinelle prospect={prospectMail}
          onFermer={() => setProspectMail(null)}
          onMailGenere={handleMailGenere}
          onGmailOuvert={(p) => setProspectAMarquerContacte(p)} />
      )}
      {showApporteurModal && (
        <CreerApporteurModal onFermer={() => setShowApporteurModal(false)} onCree={() => { setShowApporteurModal(false); charger(); }} />
      )}

      {/* Popup semi-automatique — Marquer comme contacté */}
      {prospectAMarquerContacte && (
        <div className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Mail size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="font-bold text-gray-800">Gmail ouvert</p>
                <p className="text-sm text-gray-500">Marquer <span className="font-semibold">{prospectAMarquerContacte.nom}</span> comme contacté ?</p>
              </div>
            </div>
            <p className="text-xs text-gray-400">La carte glissera automatiquement dans la colonne "Contacté" du Kanban.</p>
            <div className="flex gap-3">
              <button
                onClick={async () => {
                  try { await changerStatutProspect(prospectAMarquerContacte.id, 'contacte'); charger(); } catch { /* non bloquant */ }
                  setProspectAMarquerContacte(null);
                }}
                className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors">
                Oui, marquer contacté
              </button>
              <button
                onClick={() => setProspectAMarquerContacte(null)}
                className="flex-1 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-200 transition-colors">
                Plus tard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrmPipelinePage;
